import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Radio, Package, Users, BarChart3, Megaphone, Wallet,
  Receipt, User as UserIcon, Settings, Menu, X, Plus, Edit2, Trash2, Copy,
  ExternalLink, AlertCircle, Check, ShoppingBag, RotateCcw, Boxes, ScanLine, PackagePlus, ClipboardList, ShoppingCart, Wallet as WalletIcon,
  Search, Filter, ChevronLeft, ChevronRight, ChevronDown, Calendar as CalendarIcon,
  MoreHorizontal, Target, MessageCircle, Truck,
  Eye, EyeOff, Loader2, Film,
} from 'lucide-react';
// Phase 5.4 — simplified guided views
import SimpleHome from '@/components/seller/simple/SimpleHome';
import SimpleLive from '@/components/seller/simple/SimpleLive';
import SimpleProducts from '@/components/seller/simple/SimpleProducts';
import SimpleOrders from '@/components/seller/simple/SimpleOrders';
import SimpleInventory from '@/components/seller/simple/SimpleInventory';
import SimpleCustomers from '@/components/seller/simple/SimpleCustomers';
import ProductDetailEditor from '@/components/seller/ProductDetailEditor';
import MoreHub from '@/components/seller/simple/MoreHub';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import ImageUpload from '@/components/ImageUpload';
import BroadcastProductPicker from '@/components/BroadcastProductPicker';
import BroadcastRecordingModal, { RecordingSettingsFields, RecordingOverview, saveRecording } from '@/components/seller/live/BroadcastRecordingPanel';
import SettingsSection from '@/components/seller/SettingsSection';
import PaymentManagement from '@/components/seller/PaymentManagement';
import SellerOrdersSection from '@/components/seller/SellerOrdersSection';
import CustomersSection from '@/components/seller/CustomersSection';
import SellerRmaSection from '@/components/seller/SellerRmaSection';
import InventorySection from '@/components/seller/InventorySection';
import FulfillmentSection from '@/components/seller/FulfillmentSection';
import InboundSection from '@/components/seller/InboundSection';
import OmsWorkflowSection from '@/components/seller/OmsWorkflowSection';
import PurchaseOrderSection from '@/components/seller/PurchaseOrderSection';
import InquiriesCenter from '@/components/seller/InquiriesCenter';
import AgentAutoImport from '@/components/seller/AgentAutoImport';
// STEP GROWTH-B2 · FAN BOOST section (embedded inside AdsSection · no new page).
import FanBoostSection from '@/components/seller/FanBoostSection';
// STEP GROWTH-B3 · LIVE BOOST section (embedded inside AdsSection · no new page).
import LiveBoostSection from '@/components/seller/LiveBoostSection';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { ProductLiveVideos, UnlinkedLiveVideos } from '@/components/seller/live/LiveProductVideos';
import RecordingLibrary from '@/components/seller/videos/RecordingLibrary';
import PartialCancelPanel from '@/components/seller/PartialCancelPanel';
import { OrderRefundHistory } from '@/components/seller/CardTransactionsPanel';
// STEP 3-OPS-C · REUSE existing trackingUrl helper (LOCK · no external API · official carrier URLs only).
// STEP 3-OPS-C-HOTFIX · use internal-only label (Carrier 실시간 상태 흉내 금지).
import { getTrackingAvailable, getTrackingUrl, getInternalShipmentLabel } from '@/lib/trackingUrl';

// Phase 5.4 · Simplified 7-item primary navigation.
// Complex features (broadcasts detail, OMS, RMA, fulfillment, inbound, purchase,
// analytics, ads, wallet, transactions, profile, settings, followers) are grouped
// under "더보기" and remain fully accessible via URL params (?section=...).
const SECTIONS = [
  { key: 'home',       label: '홈',      icon: LayoutDashboard },
  { key: 'live',       label: '라이브',  icon: Radio },
  { key: 'products',   label: '상품',    icon: Package },
  { key: 'videos',     label: '녹화·영상 관리', icon: Film },
  { key: 'orders',     label: '주문',    icon: ShoppingBag },
  { key: 'payments',   label: '결제관리', icon: WalletIcon },
  { key: 'inventory',  label: '재고',    icon: Boxes },
  { key: 'customers',  label: '고객',    icon: Users },
  { key: 'inquiries',  label: 'CS 문의', icon: MessageCircle },
  { key: 'more',       label: '더보기',  icon: MoreHorizontal },
];

// Legacy section keys → primary tab key for sidebar highlighting.
const LEGACY_TO_PRIMARY = {
  broadcasts: 'live',
  oms: 'orders',
  fulfillment: 'orders',
  rma: 'orders',
  inbound: 'inventory',
  purchase: 'inventory',
  analytics: 'more',
  ads: 'more',
  wallet: 'more',
  transactions: 'more',
  profile: 'more',
  settings: 'more',
  followers: 'more',
};

const PLATFORM_OPTIONS = [
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'youtube',   label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'naver',     label: 'Naver' },
  { value: 'grip',      label: 'Grip' },
  { value: 'others',    label: '기타' },
];

const CATEGORY_OPTIONS = [
  { value: 'fashion',      label: '패션' },
  { value: 'beauty',       label: '뷰티' },
  { value: 'food',         label: '푸드' },
  { value: 'kids',         label: '키즈' },
  { value: 'pet',          label: '반려동물' },
  { value: 'electronics',  label: '전자제품' },
  { value: 'home',         label: '홈' },
  { value: 'sports',       label: '스포츠' },
  { value: 'others',       label: '기타' },
];

const BROADCAST_STATUSES = ['예정', 'LIVE', '종료', '취소'];
const PRODUCT_STATUSES = ['판매 중', '품절', '판매 종료', '비공개'];
const AD_TYPES = [
  '홈 추천 방송',
  '검색 결과 상단 노출',
  '카테고리 추천 노출',
  '방송 일정 강조',
  '셀러 프로필 추천',
  '상품 추천',
  'B2B 모집공고 광고',
];

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

const EmptyState = ({ title, subtitle, action, testId }) => (
  <div className="text-center py-12 bg-black/5 dark:bg-white/5 rounded-lg" data-testid={testId}>
    <p className="text-sm font-bold mb-1">{title}</p>
    {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-lg ${className}`}>{children}</div>
);

const StatCard = ({ label, value, testId }) => (
  <Card className="p-5">
    <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 mb-2">{label}</p>
    <p className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={testId}>{value}</p>
  </Card>
);

// ============= PHASE 5.1 · GLOBAL SEARCH + UNIFIED ORDER DETAIL =============
const _catLabel = { orders: '주문', customers: '고객', products: '상품', shipping: '배송/출고', rma: '취소·교환·반품' };

const GlobalSearch = ({ goSection, onOpenOrder, onOpenCustomer }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/seller/search', { params: { q: q.trim(), limit_per_cat: 5 } });
        setResults(data); setOpen(true);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const total = results ? Object.values(results.totals || {}).reduce((a, b) => a + b, 0) : 0;
  return (
    <div className="relative w-full max-w-xl" data-testid="global-search-wrap">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input value={q} onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        placeholder="주문번호 · 고객 · 전화 · 상품 · SKU · 송장 통합검색"
        className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm"
        data-testid="global-search-input" />
      {open && q && (
        <div className="absolute z-40 left-0 right-0 mt-1 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] shadow-2xl max-h-[70vh] overflow-y-auto"
          data-testid="global-search-results">
          <div className="flex items-center justify-between px-3 py-2 border-b border-black/5 dark:border-white/5 sticky top-0 bg-white dark:bg-[#141414]">
            <p className="text-[11px] font-bold text-gray-500">{loading ? '검색 중…' : `총 ${total.toLocaleString()}건`}</p>
            <button onClick={() => { setOpen(false); setQ(''); }} className="text-[11px] text-gray-400 hover:text-[#FF3B30]">닫기 ×</button>
          </div>
          {!loading && total === 0 && <p className="p-4 text-xs text-gray-400 text-center">검색 결과가 없습니다.</p>}
          {results && ['orders', 'customers', 'products', 'shipping', 'rma'].map((cat) => {
            const items = results[cat] || [];
            if (!items.length) return null;
            return (
              <div key={cat} className="p-2" data-testid={`search-cat-${cat}`}>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 px-2 py-1">{_catLabel[cat]} <span className="text-gray-300">({results.totals[cat]})</span></p>
                <div className="space-y-1">
                  {cat === 'orders' && items.map((o) => (
                    <button key={o.seller_order_id} onClick={() => { setOpen(false); setQ(''); onOpenOrder(o.seller_order_id); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" data-testid={`search-order-${o.seller_order_id}`}>
                      <span className="font-bold">{o.order_number}</span>
                      <span className="text-gray-500"> · {o.buyer_name_mask}{o.phone_mask ? ` · ${o.phone_mask}` : ''}</span>
                      <span className="text-gray-400"> · {o.first_item_name}{o.item_count > 1 ? ` 외 ${o.item_count - 1}건` : ''} · {won(o.total_amount)}</span>
                      <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FF3B30]/10 text-[#FF3B30]">{o.order_status}</span>
                    </button>
                  ))}
                  {cat === 'customers' && items.map((c) => (
                    <button key={c.customer_key} onClick={() => { setOpen(false); setQ(''); onOpenCustomer(c.customer_key); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" data-testid={`search-customer-${c.customer_key}`}>
                      <span className="font-bold">{c.name}</span>
                      {c.phone_mask && <span className="text-gray-500"> · {c.phone_mask}</span>}
                      <span className="text-gray-400"> · 주문 {c.orders_count}건 · {won(c.total_amount)}</span>
                    </button>
                  ))}
                  {cat === 'products' && items.map((p) => (
                    <button key={p.product_id} onClick={() => { setOpen(false); setQ(''); goSection('products'); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" data-testid={`search-product-${p.product_id}`}>
                      <span className="font-bold">{p.name}</span>
                      {p.sku && <span className="text-gray-500"> · SKU {p.sku}</span>}
                      {p.brand && <span className="text-gray-400"> · {p.brand}</span>}
                      <span className="text-gray-400"> · {won(p.sale_price)}</span>
                    </button>
                  ))}
                  {cat === 'shipping' && items.map((s) => (
                    <button key={s.seller_order_id + s.tracking_number} onClick={() => { setOpen(false); setQ(''); onOpenOrder(s.seller_order_id); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" data-testid={`search-shipping-${s.tracking_number}`}>
                      <span className="font-bold">{s.tracking_number}</span>
                      <span className="text-gray-500"> · {s.courier || '-'} · {s.buyer_name}</span>
                      <span className="text-gray-400"> · 주문 {s.order_number}</span>
                    </button>
                  ))}
                  {cat === 'rma' && items.map((r) => (
                    <button key={r.rma_id} onClick={() => { setOpen(false); setQ(''); goSection('rma'); }}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" data-testid={`search-rma-${r.rma_id}`}>
                      <span className="font-bold">{r.rma_number || r.rma_id}</span>
                      <span className="text-gray-500"> · {r.type} · {r.status}</span>
                      <span className="text-gray-400"> · {r.buyer_name} · {r.product_summary}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InquiryRow = ({ inq, onReplied }) => {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState(inq.status || 'in_progress');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/seller/inquiries/${inq.inquiry_id}/quick-reply`, { content: reply, status });
      toast.success('CS 답변이 저장되었습니다.');
      setOpen(false); setReply(''); await onReplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || '저장 실패'); }
    finally { setSaving(false); }
  };
  return (
    <div className="text-xs p-2 rounded border border-black/5 dark:border-white/5" data-testid={`uod-cs-row-${inq.inquiry_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-bold">{inq.title || '(제목 없음)'}<span className="ml-2 text-[10px] font-normal text-gray-500">· {inq.category} · {inq.status}</span></p>
          {inq.content && <p className="text-gray-500 mt-0.5 line-clamp-2">{inq.content}</p>}
        </div>
        <button onClick={() => setOpen((v) => !v)} className="text-[10px] font-bold text-[#FF3B30] hover:underline flex-shrink-0" data-testid={`uod-cs-reply-toggle-${inq.inquiry_id}`}>
          {open ? '접기' : '답변/처리'}
        </button>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/5 space-y-1.5" data-testid={`uod-cs-reply-form-${inq.inquiry_id}`}>
          <textarea placeholder="답변 또는 처리 메모" rows={2} value={reply} onChange={(e) => setReply(e.target.value)} className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`uod-cs-reply-content-${inq.inquiry_id}`} />
          <div className="flex items-center gap-1.5">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-1 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-[11px]" data-testid={`uod-cs-reply-status-${inq.inquiry_id}`}>
              <option value="new">신규</option>
              <option value="in_progress">처리중</option>
              <option value="answered">답변완료</option>
              <option value="closed">종결</option>
            </select>
            <button onClick={submit} disabled={saving} className="flex-1 py-1 bg-[#FF3B30] text-white text-[11px] font-bold rounded disabled:opacity-60" data-testid={`uod-cs-reply-submit-${inq.inquiry_id}`}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PAY_STATUS_KO = { paid: '결제완료', pending: '결제대기', refunded: '환불완료', partially_refunded: '부분환불' };
const UnifiedOrderDetail = ({ sellerOrderId, onClose, goSection }) => {
  const [data, setData] = useState(null);
  const [csForm, setCsForm] = useState({ open: false, category: 'order', title: '', content: '' });
  const [savingCs, setSavingCs] = useState(false);
  const [rmaForm, setRmaForm] = useState({ open: false, type: 'cancel', reason_code: 'seller_initiated', reason_note: '' });
  const [savingRma, setSavingRma] = useState(false);
  // STEP 2-P7-AI · Inline invoice input (reuses existing PATCH /seller/orders/{sid}/invoice
  // — SellerOrdersSection.js saveInvoice pattern). Active 3PL P7-V semantics
  // are preserved backend-side: tracking metadata saved, status/fulfillment/
  // inventory untouched. Legacy direct-ship flow unchanged.
  const [invForm, setInvForm] = useState({ open: false, courier: 'CJ대한통운', tracking_number: '' });
  const [savingInv, setSavingInv] = useState(false);
  const [cardCanceling, setCardCanceling] = useState(false);
  // STEP OMS-PARTIAL-PICK-1 · 상품별 fulfillment 배지 enrichment (선택 피킹 상태 표시).
  const [ffItems, setFfItems] = useState(null);
  useEffect(() => {
    if (!sellerOrderId) return;
    api.get(`/seller/orders/${sellerOrderId}/unified`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    // enriched per-item state (REUSE existing /seller/fulfillment/{sid}/items).
    api.get(`/seller/fulfillment/${sellerOrderId}/items`)
       .then((r) => setFfItems(r.data?.items || null))
       .catch(() => setFfItems(null));
  }, [sellerOrderId]);
  if (!sellerOrderId) return null;
  const submitInvoice = async () => {
    if (!invForm.courier || !invForm.tracking_number.trim()) {
      toast.error('택배사와 송장번호를 입력하세요.'); return;
    }
    setSavingInv(true);
    try {
      await api.patch(`/seller/orders/${sellerOrderId}/invoice`, {
        courier: invForm.courier,
        tracking_number: invForm.tracking_number.trim(),
      });
      toast.success('송장이 등록되었습니다.');
      setInvForm((f) => ({ ...f, open: false }));
      const r = await api.get(`/seller/orders/${sellerOrderId}/unified`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '송장 저장 실패'); }
    finally { setSavingInv(false); }
  };
  const submitCs = async () => {
    if (!csForm.title.trim() || !csForm.content.trim()) { toast.error('제목/내용을 입력해주세요.'); return; }
    setSavingCs(true);
    try {
      await api.post('/seller/inquiries', {
        order_id: data.order_id, seller_order_id: data.seller_order_id,
        customer_key: data.customer.customer_key,
        category: csForm.category, title: csForm.title, content: csForm.content,
        buyer_snapshot: { name: data.customer.name, phone: data.customer.phone, email: data.customer.email },
      });
      toast.success('CS 문의가 등록되었습니다.');
      setCsForm({ open: false, category: 'order', title: '', content: '' });
      const r = await api.get(`/seller/orders/${sellerOrderId}/unified`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'CS 등록 실패'); }
    finally { setSavingCs(false); }
  };
  // LIGHTPAY-OMS-CANCEL-UI-FIX-1 · 기존 라이트페이 취소 API (SellerOrdersSection 과 동일 request_id → 중복 차단).
  const doCardCancel = async () => {
    if (cardCanceling || !window.confirm('카드결제를 전액 취소할까요? 라이트페이에 실제 취소가 요청되며 되돌릴 수 없습니다.')) return;
    setCardCanceling(true);
    try {
      await api.post('/payments/lightpay/cancel', { seller_order_id: sellerOrderId, request_id: `seller-cancel:${sellerOrderId}`, reason: '판매자 결제취소' });
      toast.success('카드결제가 취소되었습니다.');
      const r = await api.get(`/seller/orders/${sellerOrderId}/unified`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '결제취소 실패'); }
    finally { setCardCanceling(false); }
  };
  const submitRma = async () => {
    setSavingRma(true);
    try {
      await api.post(`/seller/orders/${sellerOrderId}/rma`, {
        type: rmaForm.type, reason_code: rmaForm.reason_code, reason_note: rmaForm.reason_note,
      });
      toast.success('RMA 가 접수되었습니다.');
      setRmaForm({ open: false, type: 'cancel', reason_code: 'seller_initiated', reason_note: '' });
      const r = await api.get(`/seller/orders/${sellerOrderId}/unified`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'RMA 접수 실패'); }
    finally { setSavingRma(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#141414] rounded-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto p-5 space-y-4" data-testid="unified-order-detail">
        {!data ? <p className="text-center py-8 text-gray-400 text-sm">불러오는 중…</p> :
         data.error ? <p className="text-center py-8 text-[#FF3B30] text-sm">주문을 불러올 수 없습니다.</p> : (<>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">주문번호</p>
              <div className="flex items-center gap-2">
                <p className="font-black text-lg" data-testid="uod-order-number">{data.order_number}</p>
                {data.broadcast_link && (
                  <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-[#FF3B30] text-white" data-testid="uod-broadcast-badge">
                    {data.broadcast_link.is_live ? '🔴 LIVE' : '📺'} {data.broadcast_link.title || '방송'}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-[#FF3B30]" aria-label="닫기">×</button>
          </div>

          {/* 고객 / 결제 / 상태 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">고객</p>
              <p className="text-sm font-bold mt-1">{data.customer.name}</p>
              <p className="text-xs text-gray-500">{data.customer.phone || '-'}</p>
              <button onClick={() => { onClose(); goSection('customers', { customer_key: data.customer.customer_key }); }}
                className="mt-2 text-[11px] font-bold text-[#FF3B30] hover:underline"
                data-testid="uod-open-customer"
                title="Customer 360 · 주문이력 · CS · 배송지 · 메모">고객 360° · 이력 보기 →</button>
              {(data.customer_recent_orders || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/5" data-testid="uod-recent-orders">
                  <p className="text-[10px] font-bold text-gray-400 mb-1">최근 다른 주문 {data.customer_recent_orders.length}건</p>
                  <div className="space-y-1">
                    {data.customer_recent_orders.map((o) => (
                      <button key={o.seller_order_id} onClick={() => window.location.reload() || null} className="w-full text-left text-[10px] truncate hover:text-[#FF3B30]" title={o.first_item_name} data-testid={`uod-recent-${o.seller_order_id}`}>
                        <span className="font-mono">{o.order_number}</span>
                        <span className="text-gray-400"> · {o.first_item_name}{o.item_count > 1 ? ` 외 ${o.item_count - 1}` : ''} · {won(o.total_amount)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Card>
            <Card className="p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400" data-testid="uod-payment-label">
                결제 · {data.payment.provider === 'lightpay' ? `라이트페이 ${data.payment.method === 'card' ? '카드' : (data.payment.method || '')}` : (data.payment.provider || 'MOCK')}
              </p>
              <p className="text-sm font-bold mt-1" data-testid="uod-payment-status">
                {PAY_STATUS_KO[data.payment.status] || data.payment.status || '-'}{data.payment.card_name ? ` · ${data.payment.card_name}` : ''}
              </p>
              <p className="text-lg font-black text-[#FF3B30]">{won(data.payment.paid_amount ?? (data.totals || {}).total ?? 0)}</p>
              {data.payment.is_mock && <p className="text-[10px] text-gray-400 mt-1">※ PG 실연동 전</p>}
              {data.payment.provider === 'lightpay' && data.payment.status === 'paid' && ['paid', 'preparing'].includes(data.order_status) && (
                <button type="button" onClick={doCardCancel} disabled={cardCanceling}
                        className="mt-2 px-3 py-1 rounded-full border border-[#FF3B30] text-[#FF3B30] text-[11px] font-black hover:bg-[#FF3B30]/10 disabled:opacity-40"
                        data-testid="uod-card-cancel">{cardCanceling ? '취소 요청 중…' : '카드결제 취소'}</button>
              )}
              {data.payment.provider === 'lightpay' && ['paid', 'partially_refunded'].includes(data.payment.status) && ['paid', 'preparing'].includes(data.order_status) && (
                <PartialCancelPanel sellerOrderId={sellerOrderId} items={data.items}
                  onDone={async () => { const r = await api.get(`/seller/orders/${sellerOrderId}/unified`); setData(r.data); }} />
              )}
              {data.payment.refunded_amount > 0 && (
                <p className="text-[10px] text-gray-500 mt-1" data-testid="uod-refunded-amount">누적 환불 {won(data.payment.refunded_amount)} · 남은 결제 {won((data.payment.paid_amount || 0) - data.payment.refunded_amount)}</p>
              )}
              {data.payment.provider === 'lightpay' && data.payment.refunded_amount > 0 && (
                <div className="mt-2"><OrderRefundHistory sellerOrderId={sellerOrderId} /></div>
              )}
            </Card>
            <Card className="p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">주문 · 배송</p>
              <p className="text-sm font-bold mt-1">{data.order_status}</p>
              <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                {(data.shipment_master || {}).tracking_number ? (
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-[11px] truncate">{data.shipment_master.tracking_number}</span>
                    <button onClick={async () => {
                      try { await navigator.clipboard.writeText(data.shipment_master.tracking_number); toast.success('송장번호가 복사되었습니다.'); }
                      catch { toast.error('복사 실패'); }
                    }} className="p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10" data-testid="uod-copy-tracking" title="송장번호 복사">
                      <Copy size={11} />
                    </button>
                  </div>
                ) : <span>송장 미등록</span>}
                <p>분할출고 {data.counts.shipments}건</p>
                <button onClick={() => setInvForm((f) => ({ ...f, open: !f.open, tracking_number: (data.shipment_master || {}).tracking_number || '' }))}
                  className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black hover:opacity-90"
                  data-testid="uod-invoice-toggle">
                  <Truck size={10}/> {(data.shipment_master || {}).tracking_number ? '송장 수정' : '송장 입력'}
                </button>
                {/* STEP 3-OPS-C · Carrier tracking (REUSE trackingUrl helper · official URL mapping only · no external API) */}
                {(() => {
                  const sh = data.shipment_master || {};
                  if (!sh.courier && !sh.tracking_number) return null;
                  const status = getInternalShipmentLabel({ shipment: sh, orderStatus: data.order_status });
                  const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                  const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                  return (
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid="uod-tracking">
                      {sh.courier && <span className="text-[10px] text-gray-500">{sh.courier}</span>}
                      {status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold" data-testid="uod-tracking-status">{status}</span>}
                      {available && url ? (
                        <a href={url} target="_blank" rel="noreferrer noopener"
                           className="text-[11px] font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                           data-testid="uod-tracking-open">
                          배송조회 ↗
                        </a>
                      ) : sh.tracking_number ? (
                        <span className="text-[10px] text-gray-400" data-testid="uod-tracking-pending">배송조회 준비중</span>
                      ) : null}
                    </div>
                  );
                })()}
              </div>
            </Card>
          </div>

          {/* STEP 2-P7-AI · Inline invoice input (reuses PATCH /invoice · Active 3PL safe) */}
          {invForm.open && (
            <div className="rounded-lg border border-dashed border-[#FF3B30]/50 p-3 bg-[#FF3B30]/5 space-y-2" data-testid="uod-invoice-form">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                  <Truck size={10} className="inline mr-1 text-[#FF3B30]"/>택배사 · 송장번호
                </p>
                <button onClick={() => setInvForm((f) => ({ ...f, open: false }))} className="text-[10px] text-gray-500 hover:text-[#FF3B30]" data-testid="uod-invoice-cancel">닫기</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={invForm.courier} onChange={(e) => setInvForm({ ...invForm, courier: e.target.value })}
                  className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
                  data-testid="uod-invoice-courier">
                  <option value="CJ대한통운">CJ대한통운</option>
                  <option value="한진택배">한진택배</option>
                  <option value="롯데택배">롯데택배</option>
                  <option value="우체국택배">우체국택배</option>
                  <option value="로젠택배">로젠택배</option>
                  <option value="쿠팡로지스틱스">쿠팡로지스틱스</option>
                  <option value="기타">기타</option>
                </select>
                <input value={invForm.tracking_number}
                  onChange={(e) => setInvForm({ ...invForm, tracking_number: e.target.value })}
                  placeholder="송장번호"
                  className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono"
                  data-testid="uod-invoice-tracking"/>
              </div>
              <button type="button" disabled={savingInv} onClick={submitInvoice}
                className="w-full py-1.5 bg-[#FF3B30] text-white text-xs font-black rounded disabled:opacity-60"
                data-testid="uod-invoice-save">
                {savingInv ? '저장 중…' : '송장 저장'}
              </button>
              <p className="text-[10px] text-gray-500">
                * 3PL 위탁 주문은 송장정보만 저장되고, 실제 출고는 물류센터에서 처리됩니다.
              </p>
            </div>
          )}

          {/* 상품 · A4-2A-1 · 썸네일 + SKU + Barcode + 랙 위치 */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">상품 ({(data.items || []).length})</p>
            <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="uod-items">
              {(data.items || []).map((it, i) => {
                const img = it.image_snapshot || it.image || it.thumbnail || '';
                const optText = it.option && typeof it.option === 'object'
                  ? Object.entries(it.option).filter(([, v]) => v !== null && v !== '').map(([, v]) => v).join(' / ')
                  : (typeof it.option === 'string' ? it.option : '');
                // STEP OMS-PARTIAL-PICK-1 · 상품별 fulfillment 배지 · GET /fulfillment/{sid}/items REUSE.
                const _iid = it.seller_order_item_id;
                const enriched = (ffItems || []).find(
                  (x) => (_iid && x.seller_order_item_id === _iid)
                      || (x.product_id === it.product_id && (x.variant_key || 'default') === ((it.option && Object.entries(it.option).filter(([,v])=>v!==null&&v!=='').map(([k,v])=>`${k}=${v}`).sort().join('|')) || 'default'))
                );
                const _fs = enriched?.fulfillment_status;
                const _fsLabel = { ready:'출고준비', waiting_stock:'입고대기', out_of_stock:'품절',
                                   cancelled:'취소', shipped:'출고완료', picking:'피킹중', inspected:'검수완료' };
                const _fsCls = { ready:'bg-emerald-100 text-emerald-800',
                                 waiting_stock:'bg-amber-100 text-amber-800',
                                 out_of_stock:'bg-red-100 text-red-800',
                                 cancelled:'bg-gray-200 text-gray-700',
                                 shipped:'bg-blue-100 text-blue-800',
                                 picking:'bg-amber-50 text-amber-700',
                                 inspected:'bg-blue-50 text-blue-700' };
                return (
                  <div key={i} className="flex items-center gap-2 text-xs p-2 rounded border border-black/5 dark:border-white/5" data-testid={`uod-item-${i}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center justify-center">
                      {img
                        ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display='none'; }} />
                        : <Package size={14} className="text-gray-300" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold truncate" data-testid={`uod-item-name-${i}`}>
                        {it.product_name_snapshot || it.name_snapshot || it.name || '-'}
                        {optText && <span className="text-gray-400 font-normal"> · {optText}</span>}
                        {_fs && (
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded-full font-bold text-[10px] ${_fsCls[_fs] || 'bg-gray-100 text-gray-700'}`}
                                data-testid={`uod-item-ff-status-${i}`}>
                            {_fsLabel[_fs] || _fs}
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                        {it.sku && <span data-testid={`uod-item-sku-${i}`}>SKU <b className="font-mono text-gray-700 dark:text-gray-200">{it.sku}</b></span>}
                        {it.barcode && <span data-testid={`uod-item-barcode-${i}`}>Barcode <b className="font-mono text-gray-700 dark:text-gray-200">{it.barcode}</b></span>}
                        {(it.rack_location || '').trim() && (
                          <span data-testid={`uod-item-rack-${i}`}>랙 <b className="font-mono text-emerald-700 dark:text-emerald-400">{it.rack_location}</b></span>
                        )}
                        {enriched?.eta_date && (
                          <span className="text-amber-700">입고예정 {enriched.eta_date}</span>
                        )}
                      </p>
                      {/* STEP OMS-OUTBOUND-UNIFY-2 · 출고 필요수량 / 현재재고 (option 별) */}
                      {(typeof it.outbound_need === 'number' || typeof it.current_stock === 'number') && (
                        <p className="text-[10px] flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30 font-bold"
                                data-testid={`uod-item-outbound-need-${i}`}
                                title="현재 미출고 상태로 남아있는 모든 소비자 주문의 동일 상품/옵션 합계 (부분출고 완료 수량 · 취소/품절 제외)">
                            출고 필요 {it.outbound_need || 0}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full font-bold ${
                            (it.current_stock || 0) === 0
                              ? 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30'
                              : (it.current_stock || 0) < (it.outbound_need || 0)
                                ? 'bg-orange-50 text-orange-800 border border-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/30'
                                : 'bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30'
                          }`}
                                data-testid={`uod-item-current-stock-${i}`}
                                title="seller_inventory 의 (total_stock − reserved_stock) · 옵션별 · 단위: 개">
                            현재재고 {it.current_stock || 0}
                          </span>
                        </p>
                      )}
                    </div>
                    <span className="text-gray-500 flex-shrink-0 text-right">
                      <span className="block">× {it.quantity}</span>
                      <span className="block font-black text-gray-700 dark:text-gray-200">{won((it.unit_price || 0) * (it.quantity || 1))}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RMA */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">취소·교환·반품 ({data.counts.rma})</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setRmaForm((f) => ({ ...f, open: !f.open }))} className="text-[10px] font-bold text-[#FF3B30] hover:underline" data-testid="uod-toggle-rma">
                  {rmaForm.open ? '취소' : '+ RMA 접수'}
                </button>
                <button onClick={() => { onClose(); goSection('rma'); }} className="text-[10px] font-bold text-[#FF3B30] hover:underline">CS/RMA 관리 →</button>
              </div>
            </div>
            {rmaForm.open && (
              <div className="rounded-lg border border-dashed border-amber-400/50 p-2.5 space-y-2 bg-amber-50/70 dark:bg-amber-500/10 mb-2" data-testid="uod-rma-form">
                <div className="grid grid-cols-2 gap-2">
                  <select value={rmaForm.type} onChange={(e) => setRmaForm({ ...rmaForm, type: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="uod-rma-type">
                    <option value="cancel">취소</option>
                    <option value="exchange">교환</option>
                    <option value="return">반품</option>
                  </select>
                  <select value={rmaForm.reason_code} onChange={(e) => setRmaForm({ ...rmaForm, reason_code: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs">
                    <option value="seller_initiated">판매자 처리</option>
                    <option value="buyer_change_of_mind">단순 변심</option>
                    <option value="product_defect">상품 불량</option>
                    <option value="wrong_delivery">오배송</option>
                    <option value="damaged">배송 파손</option>
                    <option value="other">기타</option>
                  </select>
                </div>
                <textarea placeholder="사유 · 메모" rows={2} value={rmaForm.reason_note} onChange={(e) => setRmaForm({ ...rmaForm, reason_note: e.target.value })} className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="uod-rma-note" />
                <button type="button" disabled={savingRma} onClick={submitRma} className="w-full py-1.5 bg-amber-600 text-white text-xs font-bold rounded disabled:opacity-60" data-testid="uod-rma-submit">
                  {savingRma ? '접수 중…' : 'RMA 접수'}
                </button>
              </div>
            )}
            {data.counts.rma === 0 ? <p className="text-xs text-gray-400 py-2">관련 요청 없음</p> : (
              <div className="space-y-1" data-testid="uod-rma-list">
                {data.rma.map((r) => (
                  <div key={r.rma_id} className="text-xs p-2 rounded border border-amber-400/30 bg-amber-50/40 dark:bg-amber-500/10">
                    <span className="font-bold">{r.type}</span> · <span>{r.status}</span> · {r.rma_number || r.rma_id}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* CS 문의 */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">CS 문의 ({data.counts.inquiries})</p>
              <button onClick={() => setCsForm((f) => ({ ...f, open: !f.open }))} className="text-[10px] font-bold text-[#FF3B30] hover:underline" data-testid="uod-toggle-cs">
                {csForm.open ? '취소' : '+ CS 등록'}
              </button>
            </div>
            {csForm.open && (
              <div className="rounded-lg border border-dashed border-[#FF3B30]/40 p-2.5 space-y-2 bg-[#FF3B30]/5 mb-2" data-testid="uod-cs-form">
                <div className="grid grid-cols-2 gap-2">
                  <select value={csForm.category} onChange={(e) => setCsForm({ ...csForm, category: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs">
                    <option value="product">상품</option>
                    <option value="order">주문</option>
                    <option value="payment">결제</option>
                    <option value="delivery">배송</option>
                    <option value="cancel">취소</option>
                    <option value="exchange">교환</option>
                    <option value="return">반품</option>
                    <option value="other">기타</option>
                  </select>
                  <input placeholder="제목" value={csForm.title} onChange={(e) => setCsForm({ ...csForm, title: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="uod-cs-title" />
                </div>
                <textarea placeholder="문의 내용" rows={2} value={csForm.content} onChange={(e) => setCsForm({ ...csForm, content: e.target.value })} className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="uod-cs-content" />
                <button type="button" disabled={savingCs} onClick={submitCs} className="w-full py-1.5 bg-[#FF3B30] text-white text-xs font-bold rounded disabled:opacity-60" data-testid="uod-cs-submit">
                  {savingCs ? '등록 중…' : 'CS 문의 등록'}
                </button>
              </div>
            )}
            {data.counts.inquiries === 0 ? <p className="text-xs text-gray-400 py-2">관련 CS 없음</p> : (
              <div className="space-y-1" data-testid="uod-cs-list">
                {data.inquiries.map((q) => (
                  <InquiryRow key={q.inquiry_id} inq={q} onReplied={async () => {
                    const r = await api.get(`/seller/orders/${sellerOrderId}/unified`); setData(r.data);
                  }} />
                ))}
              </div>
            )}
          </div>
         </>)}
      </div>
    </div>
  );
};

// ============= HOME (Phase 5 Ops Dashboard) =============
const _ppct = (p) => (p >= 0 ? `+${p}%` : `${p}%`);

const TodoCard = ({ todo, tKey, onGo }) => {
  const urgent = todo.level === 'urgent';
  const active = (todo.count || 0) > 0;
  const ringCls = !active ? 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] opacity-70'
    : urgent ? 'border-[#FF3B30]/70 bg-[#FF3B30]/5 dark:bg-[#FF3B30]/10'
             : 'border-amber-400/70 bg-amber-50/70 dark:bg-amber-500/10';
  const badgeCls = urgent ? 'bg-[#FF3B30] text-white' : 'bg-amber-500 text-white';
  return (
    <button onClick={() => onGo(todo.route)} data-testid={`todo-card-${tKey}`}
      className={`w-full text-left p-4 rounded-xl border-2 transition ${ringCls} ${active ? 'hover:-translate-y-0.5' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{todo.label}</p>
          <p className="mt-1.5 text-3xl font-black tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`todo-count-${tKey}`}>
            {(todo.count || 0).toLocaleString()}<span className="text-sm font-bold text-gray-400 ml-0.5">건</span>
          </p>
        </div>
        {active && (
          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${badgeCls}`}>
            {urgent ? '긴급' : '주의'}
          </span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-gray-500 line-clamp-2">{todo.desc}</p>
    </button>
  );
};

const QuickAction = ({ label, icon: Icon, testId, onClick }) => (
  <button onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-1.5 px-3 py-2 border border-black/10 dark:border-white/10 rounded-full text-xs font-bold bg-white dark:bg-[#141414] hover:border-[#FF3B30] hover:text-[#FF3B30]">
    <Icon size={13} /> {label}
  </button>
);

const HomeSection = ({ seller, onOpenOrder }) => {
  const [ops, setOps] = useState(null);
  const [broadcastLegacy, setBroadcastLegacy] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const goSection = (key) => {
    const p = new URLSearchParams(location.search);
    p.set('section', key);
    navigate(`${location.pathname}?${p.toString()}`);
  };

  useEffect(() => {
    api.get('/seller/dashboard/ops-summary').then((r) => setOps(r.data)).catch(() => setOps({}));
    api.get('/seller/dashboard/summary').then((r) => setBroadcastLegacy(r.data)).catch(() => setBroadcastLegacy(null));
  }, []);

  if (!ops) return <EmptyState title="불러오는 중입니다" testId="home-loading" />;

  const todos = ops.todos || {};
  const order = ['outbound_waiting', 'stock_shortage', 'rma_pending', 'po_pending', 'inbound_waiting', 'order_processing'];
  const summary = ops.today_summary || {};
  const vs = summary.vs_yesterday || {};
  const todayOrders = ops.today_orders || [];
  const stockAlerts = ops.stock_alerts || [];
  const poPreview = ops.po_pending_preview || [];
  const inboundPreview = ops.inbound_preview || [];
  const notifs = ops.recent_notifications || [];

  const markNotif = async (nid) => {
    try { await api.post(`/seller/notifications/${nid}/read`); } catch { /* ignore */ }
    setOps((s) => ({ ...s, recent_notifications: (s.recent_notifications || []).map((n) => n.notif_id === nid ? { ...n, unread: false } : n) }));
  };

  return (
    <div className="space-y-6" data-testid="home-ops-dashboard">
      {/* ── 오늘 할 일 ── */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>오늘 할 일</h2>
            <p className="text-xs text-gray-500 mt-0.5">각 카드를 클릭하면 관리 메뉴로 이동합니다.</p>
          </div>
          {notifs.length > 0 && (
            <p className="text-xs text-gray-500" data-testid="unread-notif-count">
              읽지 않은 알림 <span className="font-black text-[#FF3B30]">{ops.notification_counts?.unread || 0}</span>건
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {order.map((k) => todos[k] && <TodoCard key={k} tKey={k} todo={todos[k]} onGo={goSection} />)}
        </div>
      </div>

      {/* ── 오늘 요약 (주문금액 · 결제완료 기준) ── */}
      <div>
        <h3 className="text-sm font-black tracking-widest uppercase text-gray-500 mb-2" data-testid="today-summary-title">오늘 운영 요약</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="today-summary-cards">
          {[
            { key: 'orders', label: '오늘 주문',        value: summary.orders,      delta: vs.orders },
            { key: 'qty',    label: '오늘 판매 수량',    value: summary.qty,         delta: vs.qty },
            { key: 'paid',   label: '결제완료 주문금액', value: won(summary.paid_amount || 0), delta: vs.paid_amount, isMoney: true },
            { key: 'shipped',label: '오늘 출고 완료',    value: summary.shipped,     delta: vs.shipped },
            { key: 'rma',    label: '오늘 신규 CS/RMA',  value: summary.rma_new,     delta: null },
          ].map((s) => (
            <Card key={s.key} className="p-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{s.label}</p>
              <p className="mt-1 text-xl font-black tabular-nums" data-testid={`today-stat-${s.key}`}>
                {typeof s.value === 'number' ? s.value.toLocaleString() : s.value}
              </p>
              {s.delta && (
                <p className={`mt-0.5 text-[11px] font-bold ${s.delta.diff > 0 ? 'text-emerald-500' : s.delta.diff < 0 ? 'text-[#FF3B30]' : 'text-gray-400'}`}>
                  {_ppct(s.delta.pct)} 어제 대비
                </p>
              )}
            </Card>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-gray-400">※ PG 실결제 연동 전 · MOCK 결제 상태 기준 <b>주문금액</b> 을 표시합니다.</p>
      </div>

      {/* ── 오늘 처리할 주문 · 재고/발주 알림 (2-col) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4" >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black tracking-widest uppercase">오늘 처리할 주문</h3>
            <button onClick={() => goSection('orders')} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="btn-view-all-orders">전체 주문 보기 →</button>
          </div>
          {todayOrders.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">지금 처리할 주문이 없습니다.</p>
          ) : (
            <div className="space-y-2" data-testid="today-orders-list">
              {todayOrders.map((o) => (
                <button key={o.seller_order_id} onClick={() => onOpenOrder(o.seller_order_id)} className="w-full text-left p-2.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 border border-black/5 dark:border-white/5" data-testid={`today-order-${o.seller_order_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold text-gray-500">{o.order_number} · {o.buyer_name}</p>
                      <p className="text-sm font-semibold truncate mt-0.5">{o.first_item_name}{o.item_count > 1 ? ` 외 ${o.item_count - 1}건` : ''}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">수량 {o.total_qty}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">{o.payment_status === 'paid' ? '결제완료' : (o.payment_status || o.order_status)}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#FF3B30]/10 text-[#FF3B30]">{o.work_status}</span>
                      </div>
                    </div>
                    <p className="text-sm font-black flex-shrink-0">{won(o.total_amount)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black tracking-widest uppercase">재고/발주 알림</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => goSection('inventory')} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="btn-view-inventory">재고 →</button>
              <button onClick={() => goSection('purchase')} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="btn-view-purchase">발주 →</button>
            </div>
          </div>
          {(stockAlerts.length + poPreview.length + inboundPreview.length) === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">지금은 재고/발주 관련 알림이 없습니다.</p>
          ) : (
            <div className="space-y-3" data-testid="stock-po-alerts">
              {stockAlerts.length > 0 && (
                <div>
                  <p className="text-[10px] font-black tracking-widest uppercase text-[#FF3B30] mb-1.5">재고 부족 ({stockAlerts.length})</p>
                  <div className="space-y-1.5">
                    {stockAlerts.map((s) => (
                      <button key={`${s.product_id}-${s.variant_key}`} onClick={() => goSection('inventory')} className="w-full text-left flex items-center justify-between gap-2 p-2 rounded border border-black/5 dark:border-white/5 hover:border-[#FF3B30]/40" data-testid={`stock-alert-${s.product_id}`}>
                        <span className="min-w-0 flex-1 truncate">
                          <span className="text-xs font-semibold">{s.product_name}</span>
                          {s.variant_key !== 'default' && <span className="text-[10px] text-gray-500"> · {s.variant_key}</span>}
                        </span>
                        <span className="text-[10px] font-bold text-gray-500 flex-shrink-0">
                          가능 {s.available_stock} · 부족 {s.unfulfilled_qty || 0} · 권장 <span className="text-[#FF3B30]">{s.recommended || 0}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {poPreview.length > 0 && (
                <div>
                  <p className="text-[10px] font-black tracking-widest uppercase text-amber-600 mb-1.5">발주 승인 대기 ({poPreview.length})</p>
                  <div className="space-y-1.5">
                    {poPreview.map((p) => (
                      <button key={p.po_id} onClick={() => goSection('purchase')} className="w-full text-left flex items-center justify-between gap-2 p-2 rounded border border-black/5 dark:border-white/5 hover:border-amber-400" data-testid={`po-alert-${p.po_id}`}>
                        <span className="min-w-0 flex-1 truncate">
                          <span className="text-xs font-semibold">{p.po_number}</span>
                          <span className="text-[10px] text-gray-500"> · {(p.supplier_snapshot || {}).name || '-'} · {p.item_count}건</span>
                        </span>
                        <span className="text-xs font-black flex-shrink-0">{won(p.total_amount || 0)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {inboundPreview.length > 0 && (
                <div>
                  <p className="text-[10px] font-black tracking-widest uppercase text-sky-600 mb-1.5">입고 대기 ({inboundPreview.length})</p>
                  <div className="space-y-1.5">
                    {inboundPreview.map((r) => (
                      <button key={r.inbound_id} onClick={() => goSection('inbound')} className="w-full text-left flex items-center justify-between gap-2 p-2 rounded border border-black/5 dark:border-white/5 hover:border-sky-400" data-testid={`inbound-alert-${r.inbound_id}`}>
                        <span className="min-w-0 flex-1 truncate">
                          <span className="text-xs font-semibold">{r.product_name_snapshot || '-'}</span>
                          <span className="text-[10px] text-gray-500"> · {r.supplier || '-'}</span>
                        </span>
                        <span className="text-[10px] font-bold text-gray-500 flex-shrink-0">잔여 {r.remaining_qty} / {r.expected_qty}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* ── 최근 알림 + 빠른 실행 ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black tracking-widest uppercase">최근 알림</h3>
            <p className="text-[11px] text-gray-500">읽지 않음 <span className="font-black text-[#FF3B30]">{ops.notification_counts?.unread || 0}</span></p>
          </div>
          {notifs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">최근 알림이 없습니다.</p>
          ) : (
            <div className="space-y-1.5" data-testid="recent-notifications-list">
              {notifs.map((n) => (
                <button key={n.notif_id}
                  onClick={() => { markNotif(n.notif_id); if (n.type === 'reorder_shortage') goSection('inventory'); else if (n.type === 'po_approval_needed') goSection('purchase'); else goSection('inventory'); }}
                  className={`w-full text-left flex items-center gap-2 p-2 rounded border ${n.unread ? 'border-[#FF3B30]/40 bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/5'} hover:border-[#FF3B30]/60`}
                  data-testid={`notif-${n.notif_id}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${n.unread ? 'bg-[#FF3B30]' : 'bg-gray-300'}`} />
                  <span className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">{n.title}</p>
                    {n.body && <p className="text-[10px] text-gray-500 truncate">{n.body}</p>}
                  </span>
                  <span className="text-[10px] text-gray-400 flex-shrink-0">{n.created_at ? format(new Date(n.created_at), 'M/d HH:mm', { locale: ko }) : ''}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-black tracking-widest uppercase mb-3">빠른 실행</h3>
          <div className="flex flex-wrap gap-2" data-testid="quick-actions">
            <QuickAction label="상품 등록" icon={Plus}          testId="qa-add-product"   onClick={() => goSection('products')} />
            <QuickAction label="재고 확인" icon={Boxes}          testId="qa-inventory"     onClick={() => goSection('inventory')} />
            <QuickAction label="발주 승인" icon={ShoppingCart}   testId="qa-purchase"      onClick={() => goSection('purchase')} />
            <QuickAction label="주문 확인" icon={ShoppingBag}    testId="qa-orders"        onClick={() => goSection('orders')} />
            <QuickAction label="출고 처리" icon={ScanLine}       testId="qa-fulfillment"   onClick={() => goSection('fulfillment')} />
            <QuickAction label="방송 등록" icon={Radio}          testId="qa-broadcasts"    onClick={() => goSection('broadcasts')} />
          </div>
        </Card>
      </div>

      {/* ── 기존 방송/충전금 카드 (legacy) ── */}
      {broadcastLegacy && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="broadcast-legacy">
          <StatCard label="오늘 예정 방송" value={broadcastLegacy.today_scheduled} testId="stat-today-scheduled" />
          <StatCard label="현재 LIVE"      value={broadcastLegacy.live_now}         testId="stat-live-now" />
          <StatCard label="총 팔로워"      value={broadcastLegacy.total_followers?.toLocaleString()} testId="stat-followers" />
          <StatCard label="보유 충전금"    value={won(broadcastLegacy.wallet_balance)} testId="stat-wallet-balance" />
        </div>
      )}
    </div>
  );
};

// ============= BROADCASTS =============

// Datetime helpers — Asia/Seoul is the platform's canonical timezone.
// The datetime-local <input> is displayed in Seoul time; on submit we
// convert to a proper UTC ISO string, and on load we convert the UTC ISO
// from the API back to Seoul-local for the input. This prevents the
// 9-hour drift that used to happen every time the modal opened.
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const toKstLocalInput = (iso) => {
  if (!iso) return '';
  const withZ = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(withZ);
  if (isNaN(d.getTime())) return '';
  const kst = new Date(d.getTime() + KST_OFFSET_MS);
  return kst.toISOString().slice(0, 16);
};
const fromKstLocalInput = (local) => {
  if (!local) return null;
  const [datePart, timePart = '00:00'] = local.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, hh - 9, mm, 0)).toISOString();
};

const KAKAO_TARGET_SCOPE_LABELS = { all: '전체 팔로워', '6m': '최근 6개월 팔로워', '1y': '최근 1년 팔로워', custom: '직접 기간 설정' };
const KAKAO_SEND_MODE_LABELS = { now: '지금 발송', scheduled: '예약 발송', on_live_start: 'LIVE 시작 시' };
const KAKAO_IMAGE_SOURCE_LABELS = { broadcast: '방송 대표 이미지', product: '연결 상품 이미지', custom: '별도 이미지' };

const BroadcastForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    title: '', description: '', category: 'others',
    scheduled_at: '', duration: 60, is_public: true,
    stream_url: '', thumbnail: '',
    // Home schedule promotion (free tier — paid promotion structure kept server-side)
    home_schedule_image_url: '', home_schedule_title: '',
    promotion_text: '', promotion_badge_text: '', promotion_text_color: '#FF3B30',
    home_schedule_visible: true,
    // STEP KAKAO-BROADCAST-P1 · Kakao 방송알림 설정 defaults (실 발송 X)
    kakao_alert_enabled: false,
    kakao_alert_image_source: 'broadcast',
    kakao_alert_image_url: '',
    kakao_alert_notice_text: '',
    kakao_alert_event_text: '',
    kakao_alert_target_scope: 'all',
    kakao_alert_target_from: '',
    kakao_alert_target_to: '',
    kakao_alert_send_mode: 'on_live_start',
    kakao_alert_send_at: '',
    ...(initial || {}),
    // Overwrite scheduled_at with the KST-formatted input value so the browser
    // does not misinterpret a naive UTC string as local time.
    scheduled_at: initial?.scheduled_at ? toKstLocalInput(initial.scheduled_at) : '',
    // Kakao datetime 도 KST 로 render (편집 진입 시 값 복원용).
    kakao_alert_send_at:    initial?.kakao_alert_send_at    ? toKstLocalInput(initial.kakao_alert_send_at)    : '',
    kakao_alert_target_from: initial?.kakao_alert_target_from ? toKstLocalInput(initial.kakao_alert_target_from) : '',
    kakao_alert_target_to:   initial?.kakao_alert_target_to   ? toKstLocalInput(initial.kakao_alert_target_to)   : '',
  }));
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // LIVE-SELLER-RECORDING-TEST-FLOW-1 · 녹화 설정은 기존 PUT /recording 으로만 저장.
  const recInit = { enabled: !!initial?.recording_enabled, mode: initial?.recording_mode || 'composite', auto: initial?.recording_auto_split !== false };
  const [rec, setRec] = useState(recInit);
  const recLocked = ['종료', 'ended', '취소', 'cancelled'].includes(initial?.status);
  // STEP KAKAO-BROADCAST-P2A · 선택된 팔로워 수 (실데이터 · debounce · scope 변경 시 재조회)
  const [followerCount, setFollowerCount] = useState({ status: 'idle', value: null });
  const [dryRun, setDryRun] = useState({ status: 'idle', data: null });
  const isEdit = !!initial?.schedule_id;
  useUnsavedGuard(dirty && !saving);

  // scope / custom-date 변경 시 실제 follower count 재조회. custom 은 debounce.
  useEffect(() => {
    if (!form.kakao_alert_enabled) { setFollowerCount({ status: 'idle', value: null }); return; }
    const scope = form.kakao_alert_target_scope;
    if (!scope) return;
    let cancelled = false;
    const fetchCount = async () => {
      const params = { scope };
      if (scope === 'custom') {
        const f = fromKstLocalInput(form.kakao_alert_target_from);
        const t = fromKstLocalInput(form.kakao_alert_target_to);
        if (!f || !t) { setFollowerCount({ status: 'idle', value: null }); return; }
        params.from = f;
        params.to = t;
      }
      setFollowerCount({ status: 'loading', value: null });
      try {
        // Note: axios serializes params → GET /seller/followers/summary?scope=...
        // `from` 은 backend 에서 `from_` alias 로 받는다 (query param 은 자동 매핑됨).
        const r = await api.get('/seller/followers/summary', { params });
        if (cancelled) return;
        setFollowerCount({
          status: 'ok',
          value: r.data?.selected_follower_count ?? 0,
          eligible: r.data?.kakao_eligible_count ?? undefined,
        });
      } catch (e) {
        if (!cancelled) setFollowerCount({ status: 'error', value: null,
          error: e?.response?.data?.detail || '조회 실패' });
      }
    };
    // custom 은 400ms debounce · 나머지는 즉시.
    const delay = scope === 'custom' ? 400 : 0;
    const t = setTimeout(fetchCount, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.kakao_alert_enabled, form.kakao_alert_target_scope,
      form.kakao_alert_target_from, form.kakao_alert_target_to]);

  const setFormDirty = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const utcIso = fromKstLocalInput(form.scheduled_at);
      if (!utcIso) {
        toast.error('방송 시작 시간을 입력해주세요.');
        setSaving(false);
        return;
      }
      const durMin = Number(form.duration) || 60;
      const homeVisible = form.home_schedule_visible !== false;
      // STEP KAKAO-BROADCAST-P1 · Kakao 방송알림 필드 KST → UTC 변환 · body 병합.
      const kakaoBody = form.kakao_alert_enabled ? {
        kakao_alert_enabled:       true,
        kakao_alert_image_source:  form.kakao_alert_image_source || 'broadcast',
        kakao_alert_image_url:     form.kakao_alert_image_source === 'custom' ? (form.kakao_alert_image_url || '') : '',
        kakao_alert_notice_text:   (form.kakao_alert_notice_text || '').slice(0, 200),
        kakao_alert_event_text:    (form.kakao_alert_event_text || '').slice(0, 200),
        kakao_alert_target_scope:  form.kakao_alert_target_scope || 'all',
        kakao_alert_target_from:   form.kakao_alert_target_scope === 'custom' ? (fromKstLocalInput(form.kakao_alert_target_from) || null) : null,
        kakao_alert_target_to:     form.kakao_alert_target_scope === 'custom' ? (fromKstLocalInput(form.kakao_alert_target_to)   || null) : null,
        kakao_alert_send_mode:     form.kakao_alert_send_mode || 'on_live_start',
        kakao_alert_send_at:       form.kakao_alert_send_mode === 'scheduled' ? (fromKstLocalInput(form.kakao_alert_send_at) || null) : null,
      } : { kakao_alert_enabled: false };
      // Send both duration and duration_minutes; mirror both visibility fields.
      const body = {
        ...form,
        scheduled_at: utcIso,
        duration: durMin,
        duration_minutes: durMin,
        is_public: !!form.is_public,
        home_schedule_visible: homeVisible,
        schedule_visible: homeVisible,
        ...kakaoBody,
      };
      let res;
      if (isEdit) {
        res = await api.put(`/seller/broadcasts/${initial.schedule_id}`, body);
      } else {
        res = await api.post('/seller/broadcasts', body);
      }
      // Only celebrate when the server actually matched the target row.
      if (isEdit) {
        const matched = res?.data?.matched_count;
        if (matched === 0) throw new Error('저장 대상 방송을 찾을 수 없습니다.');
      }
      const sid = isEdit ? initial.schedule_id : res?.data?.schedule_id;
      const recChanged = rec.enabled !== recInit.enabled || (rec.enabled && (rec.mode !== recInit.mode || rec.auto !== recInit.auto));
      if (sid && recChanged && !recLocked && !(await saveRecording(sid, rec))) {
        setSaving(false);
        onSaved?.();
        return;
      }
      toast.success(isEdit ? '방송이 수정되었습니다.' : '방송이 등록되었습니다.');
      setJustSaved(true);
      setDirty(false);
      onSaved?.();
      setTimeout(() => { setJustSaved(false); onClose(); }, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || '저장에 실패했습니다. 다시 시도해주세요.');
    } finally { setSaving(false); }
  };

  const requestClose = () => {
    if (dirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 페이지를 나가시겠습니까?')) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={requestClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white dark:bg-[#141414] rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" data-testid="broadcast-form">
        <h3 className="text-xl font-black tracking-tight mb-4">{isEdit ? '방송 수정' : '방송 등록'}</h3>
        <div className="space-y-3">
          <input required placeholder="방송 제목" value={form.title} onChange={(e) => setFormDirty({ title: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="bcast-title" />
          <textarea placeholder="방송 설명" value={form.description} onChange={(e) => setFormDirty({ description: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" rows={3} data-testid="bcast-desc" />
          <ImageUpload value={form.thumbnail} onChange={(url) => setFormDirty({ thumbnail: url })} kind="broadcast" label="방송 대표 이미지" aspectClass="aspect-video" testId="bcast-thumbnail" />
          <input required type="datetime-local" value={form.scheduled_at || ''} onChange={(e) => setFormDirty({ scheduled_at: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="bcast-scheduled" />
          <input type="number" min="10" placeholder="예상 진행 시간(분)" value={form.duration} onChange={(e) => setFormDirty({ duration: parseInt(e.target.value || 0) })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
          <select value={form.category} onChange={(e) => setFormDirty({ category: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="bcast-category">
            {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {/* Prod-3.2 · 플랫폼 / 방송 URL / 상태(LIVE·종료) 는 YouTube OAuth 자동감지가
              자동으로 채웁니다. 판매자가 직접 선택하지 않도록 UI 에서 제거.
              stream_url 은 자동감지 실패 시 fallback 을 위해 "고급 설정" 아래에 유지. */}
          <div className="text-[11px] text-gray-500 dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 rounded px-3 py-2 leading-relaxed" data-testid="bcast-auto-info">
            플랫폼 · 방송 URL · LIVE/종료 상태는 연결된 YouTube 계정의 자동감지로 자동 관리됩니다. 예정 상태로 저장 후, 실제 방송을 시작하면 자동으로 LIVE 로 전환됩니다.
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setFormDirty({ is_public: e.target.checked })} className="accent-[#FF3B30]" data-testid="bcast-public" /> 공개 방송
          </label>

          {/* 홈 편성표 홍보 — free tier now; server retains paid-promotion fields for future ads */}
          <div className="border-t border-black/5 dark:border-white/10 pt-4 mt-2 space-y-3" data-testid="home-schedule-promo">
            <div className="flex items-center gap-2">
              <p className="text-sm font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>홈 편성표 홍보</p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF3B30] bg-[#FF3B30]/10 px-1.5 py-0.5 rounded">무료</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">홈 화면 '오늘 방송 예정' 슬라이더에 표시되는 홍보 카드를 설정합니다. 등록하지 않으면 방송 제목·썸네일이 자동 사용됩니다.</p>

            <ImageUpload
              value={form.home_schedule_image_url}
              onChange={(url) => setFormDirty({ home_schedule_image_url: url })}
              kind="broadcast"
              label="홈 편성표 홍보 이미지 (권장 16:9, JPG/PNG/WEBP, 최대 5MB)"
              aspectClass="aspect-video"
              testId="bcast-home-image"
            />
            <input
              placeholder="홈 편성표 카드 제목 (미입력 시 방송 제목 사용)"
              value={form.home_schedule_title}
              maxLength={40}
              onChange={(e) => setFormDirty({ home_schedule_title: e.target.value })}
              className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
              data-testid="bcast-home-title"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                placeholder="강조 문구 (최대 24자, 예: 오늘만 특가)"
                value={form.promotion_text}
                maxLength={24}
                onChange={(e) => setFormDirty({ promotion_text: e.target.value.slice(0, 24) })}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                data-testid="bcast-promo-text"
              />
              <input
                placeholder="배지 문구 (최대 10자, 예: 특가)"
                value={form.promotion_badge_text}
                maxLength={10}
                onChange={(e) => setFormDirty({ promotion_badge_text: e.target.value.slice(0, 10) })}
                className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                data-testid="bcast-promo-badge"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500 dark:text-gray-400">배지 색상</label>
              <input
                type="color"
                value={form.promotion_text_color || '#FF3B30'}
                onChange={(e) => setFormDirty({ promotion_text_color: e.target.value })}
                className="h-8 w-12 rounded border border-black/10 dark:border-white/10 cursor-pointer"
                data-testid="bcast-promo-color"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.home_schedule_visible !== false}
                onChange={(e) => setFormDirty({ home_schedule_visible: e.target.checked })}
                className="accent-[#FF3B30]"
                data-testid="bcast-home-visible"
              />
              홈 편성표에 노출
            </label>
          </div>

          {/* Prod-3.2 · 고급 설정 (수동 연결) — 자동감지 실패 시 fallback 전용 */}
          <div className="border-t border-black/5 dark:border-white/10 pt-4 mt-2 space-y-3" data-testid="bcast-recording-section">
            <RecordingSettingsFields value={rec} onChange={(v) => { setRec(v); setDirty(true); }} disabled={recLocked || saving}
                                     reason={recLocked ? '종료·취소된 방송은 녹화 설정을 바꿀 수 없어요.' : 'LIVE 시작 후에는 녹화 설정을 바꿀 수 없어요.'} />
            {isEdit && <RecordingOverview scheduleId={initial.schedule_id} />}
          </div>
          {/* STEP KAKAO-BROADCAST-P1 · 카카오 방송알림 (foundation only · 실 발송 X) */}
          <div className="border-t border-black/5 dark:border-white/10 pt-4 mt-2 space-y-3"
               data-testid="bcast-kakao-alert-section">
            <div className="flex items-center gap-2">
              <p className="text-sm font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                카카오 방송알림
              </p>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded">
                준비 중
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              팔로워에게 방송 안내 카카오 알림을 보낼 수 있습니다. 설정은 지금 저장할 수 있으며, 실제 발송은 카카오 승인 완료 이후 활성화됩니다.
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.kakao_alert_enabled === true}
                onChange={(e) => setFormDirty({ kakao_alert_enabled: e.target.checked })}
                className="accent-[#FF3B30]"
                data-testid="bcast-kakao-alert-toggle"
              />
              카카오 방송알림 사용
            </label>

            {form.kakao_alert_enabled && (
              <div className="space-y-3 pl-1 border-l-2 border-amber-500/40 pl-3"
                   data-testid="bcast-kakao-alert-details">
                {/* 이미지 소스 */}
                <div>
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-1">알림 이미지</p>
                  <div className="flex flex-wrap gap-3">
                    {['broadcast', 'product', 'custom'].map((s) => (
                      <label key={s} className="inline-flex items-center gap-1 text-xs">
                        <input
                          type="radio"
                          name="kakao_alert_image_source"
                          value={s}
                          checked={form.kakao_alert_image_source === s}
                          onChange={() => setFormDirty({ kakao_alert_image_source: s })}
                          className="accent-[#FF3B30]"
                          data-testid={`bcast-kakao-img-src-${s}`}
                        />
                        {KAKAO_IMAGE_SOURCE_LABELS[s]}
                      </label>
                    ))}
                  </div>
                  {form.kakao_alert_image_source === 'custom' && (
                    <div className="mt-2">
                      <ImageUpload
                        value={form.kakao_alert_image_url}
                        onChange={(url) => setFormDirty({ kakao_alert_image_url: url })}
                        kind="broadcast"
                        label="별도 알림 이미지 (권장 1:1 / JPG,PNG,WEBP)"
                        aspectClass="aspect-square"
                        testId="bcast-kakao-img-custom"
                      />
                    </div>
                  )}
                </div>

                {/* 기본 안내 · 승인 템플릿 */}
                <div className="text-[11px] text-gray-500 dark:text-gray-400 bg-black/[0.03] dark:bg-white/5 rounded px-3 py-2 leading-relaxed"
                     data-testid="bcast-kakao-approved-notice">
                  기본 안내 문구는 승인된 카카오 알림톡 템플릿을 사용합니다. 판매자는 아래 <b>공지</b>·<b>이벤트</b> 영역만 입력할 수 있습니다.
                </div>

                {/* 공지 · 이벤트 */}
                <div className="grid grid-cols-1 gap-2">
                  <label className="block">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200">공지 내용 (선택 · 최대 200자)</span>
                    <textarea
                      rows={2}
                      maxLength={200}
                      value={form.kakao_alert_notice_text || ''}
                      onChange={(e) => setFormDirty({ kakao_alert_notice_text: e.target.value })}
                      placeholder="예) 오늘 라이브 구매 고객 무료배송"
                      className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                      data-testid="bcast-kakao-notice"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200">이벤트 내용 (선택 · 최대 200자)</span>
                    <textarea
                      rows={2}
                      maxLength={200}
                      value={form.kakao_alert_event_text || ''}
                      onChange={(e) => setFormDirty({ kakao_alert_event_text: e.target.value })}
                      placeholder="예) 방송 중 구매 시 특별 할인"
                      className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                      data-testid="bcast-kakao-event"
                    />
                  </label>
                </div>

                {/* 발송 대상 */}
                <div>
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-1">발송 대상</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {['all', '6m', '1y', 'custom'].map((v) => (
                      <label key={v} className="inline-flex items-center gap-1 text-xs">
                        <input
                          type="radio"
                          name="kakao_alert_target_scope"
                          value={v}
                          checked={form.kakao_alert_target_scope === v}
                          onChange={() => setFormDirty({ kakao_alert_target_scope: v })}
                          className="accent-[#FF3B30]"
                          data-testid={`bcast-kakao-target-${v}`}
                        />
                        {KAKAO_TARGET_SCOPE_LABELS[v]}
                      </label>
                    ))}
                  </div>
                  {form.kakao_alert_target_scope === 'custom' && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="datetime-local"
                        value={form.kakao_alert_target_from || ''}
                        onChange={(e) => setFormDirty({ kakao_alert_target_from: e.target.value })}
                        className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                        data-testid="bcast-kakao-target-from"
                      />
                      <input
                        type="datetime-local"
                        value={form.kakao_alert_target_to || ''}
                        onChange={(e) => setFormDirty({ kakao_alert_target_to: e.target.value })}
                        className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                        data-testid="bcast-kakao-target-to"
                      />
                    </div>
                  )}
                  <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400"
                     data-testid="bcast-kakao-target-count-placeholder">
                    {followerCount.status === 'loading' && '대상 확인 중...'}
                    {followerCount.status === 'error' && (
                      <span className="text-amber-700">
                        팔로워 수를 확인하지 못했습니다.
                      </span>
                    )}
                    {followerCount.status === 'ok' && (
                      <>
                        선택된 팔로워 <b data-testid="bcast-kakao-selected-count">{(followerCount.value ?? 0).toLocaleString()}</b>명
                        {typeof followerCount.eligible === 'number' && (
                          <>
                            {' · '}카카오 발송 가능 <b data-testid="bcast-kakao-eligible-count">{followerCount.eligible.toLocaleString()}</b>명
                          </>
                        )}
                      </>
                    )}
                    {followerCount.status === 'idle' && form.kakao_alert_target_scope === 'custom' && '기간을 입력하면 인원이 계산됩니다.'}
                  </p>
                  <p className="mt-0.5 text-[9px] text-gray-400 leading-relaxed">
                    ※ 수신동의와 유효한 휴대폰번호 등 현재 발송 조건을 충족한 인원입니다. 실제 발송 결과는 카카오 처리 상태에 따라 달라질 수 있습니다.
                  </p>
                  {/* STEP KAKAO-BROADCAST-P3 · 승인 대기 배지 + Mock dry-run */}
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5"
                       data-testid="bcast-kakao-p3-status">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                      <span className="text-[10px] font-black text-amber-900 dark:text-amber-200">
                        카카오 승인 대기 · Mock
                      </span>
                    </div>
                    <button type="button"
                            disabled={!isEdit || dryRun.status === 'loading'}
                            onClick={async () => {
                              if (!isEdit) return;
                              setDryRun({ status: 'loading', data: null });
                              try {
                                const r = await api.post(`/seller/broadcasts/${initial.schedule_id}/kakao-alert/dry-run`);
                                setDryRun({ status: 'ok', data: r.data });
                                toast.success(`Mock 발송 확인 · 대상 ${r.data?.eligible_count ?? 0}명 (실제 카카오톡은 발송되지 않았습니다)`);
                              } catch (e) {
                                setDryRun({ status: 'error', data: null,
                                  error: e?.response?.data?.detail || '실패' });
                                toast.error(e?.response?.data?.detail || 'Mock 발송 확인 실패');
                              }
                            }}
                            className="text-[10px] font-black px-2 py-1 rounded bg-amber-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="bcast-kakao-dry-run-btn">
                      {dryRun.status === 'loading' ? '확인 중...' : '테스트 발송 확인'}
                    </button>
                  </div>
                  <p className="mt-1 text-[9px] text-amber-800/80 dark:text-amber-200/80 leading-relaxed"
                     data-testid="bcast-kakao-p3-notice">
                    현재 카카오 알림톡 승인 준비 단계입니다. 설정 및 대상 확인은 가능하지만 <b>실제 카카오톡은 발송되지 않습니다</b>. {!isEdit && '방송을 먼저 저장한 뒤 테스트 발송 확인을 사용할 수 있습니다.'}
                  </p>
                  {dryRun.status === 'ok' && dryRun.data && (
                    <div className="mt-1 text-[10px] rounded border border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] px-2 py-1.5"
                         data-testid="bcast-kakao-dry-run-result">
                      <p className="font-black text-gray-700 dark:text-gray-200">Mock 발송 결과</p>
                      <p className="text-gray-600 dark:text-gray-400">
                        발송 가능 <b data-testid="bcast-kakao-dry-run-eligible">{dryRun.data.eligible_count}</b>명 · Mock 전송 <b data-testid="bcast-kakao-dry-run-sent">{dryRun.data.counts?.sent ?? 0}</b>건 · 상태 <b>{dryRun.data.external_status}</b>
                      </p>
                    </div>
                  )}
                </div>

                {/* 발송 시점 */}
                <div>
                  <p className="text-xs font-bold text-gray-700 dark:text-gray-200 mb-1">발송 시점</p>
                  <div className="flex flex-wrap gap-3">
                    {['now', 'scheduled', 'on_live_start'].map((v) => (
                      <label key={v} className="inline-flex items-center gap-1 text-xs">
                        <input
                          type="radio"
                          name="kakao_alert_send_mode"
                          value={v}
                          checked={form.kakao_alert_send_mode === v}
                          onChange={() => setFormDirty({ kakao_alert_send_mode: v })}
                          className="accent-[#FF3B30]"
                          data-testid={`bcast-kakao-send-${v}`}
                        />
                        {KAKAO_SEND_MODE_LABELS[v]}
                      </label>
                    ))}
                  </div>
                  {form.kakao_alert_send_mode === 'scheduled' && (
                    <input
                      type="datetime-local"
                      value={form.kakao_alert_send_at || ''}
                      onChange={(e) => setFormDirty({ kakao_alert_send_at: e.target.value })}
                      className="mt-2 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                      data-testid="bcast-kakao-send-at"
                    />
                  )}
                  <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-400"
                     data-testid="bcast-kakao-dispatch-notice">
                    현재 발송 기능 준비 중 · 저장된 설정만 유지됩니다.
                  </p>
                </div>

                {/* Preview */}
                <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-white dark:bg-[#0A0A0A]"
                     data-testid="bcast-kakao-preview">
                  <p className="text-[10px] font-black text-gray-500 mb-2">미리보기</p>
                  <div className="aspect-video rounded bg-black/[0.06] dark:bg-white/10 flex items-center justify-center mb-2">
                    <p className="text-[10px] text-gray-500">
                      {KAKAO_IMAGE_SOURCE_LABELS[form.kakao_alert_image_source] || '방송 대표 이미지'}
                    </p>
                  </div>
                  <p className="text-xs font-black text-black dark:text-white">
                    {form.title || 'OO 판매자'} LIVE 방송 안내
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    오늘 준비한 상품을 라이브에서 만나보세요.
                  </p>
                  {form.kakao_alert_notice_text && (
                    <p className="mt-1.5 text-xs">
                      <b>[공지]</b> {form.kakao_alert_notice_text}
                    </p>
                  )}
                  {form.kakao_alert_event_text && (
                    <p className="mt-1 text-xs">
                      <b>[이벤트]</b> {form.kakao_alert_event_text}
                    </p>
                  )}
                  <button type="button" disabled
                          className="mt-2 w-full py-1.5 rounded border border-black/10 dark:border-white/10 text-xs font-bold opacity-70 cursor-not-allowed"
                          data-testid="bcast-kakao-preview-cta">
                    방송 보러가기
                  </button>
                  <p className="mt-2 text-[9px] text-gray-400 leading-relaxed">
                    ※ 실제 발송 문구는 승인 템플릿에 따라 달라질 수 있습니다. 방송 보러가기 링크는 <code>/live/{'{'}schedule_id{'}'}</code> 로 자동 생성됩니다.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Prod-3.2 · 고급 설정 (수동 연결) — 자동감지 실패 시 fallback 전용 */}
          <div className="border-t border-black/5 dark:border-white/10 pt-4 mt-2" data-testid="bcast-advanced-section">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="w-full flex items-center justify-between text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white"
              data-testid="bcast-advanced-toggle"
            >
              <span>고급 설정 (수동 연결)</span>
              <span className="text-xs">{advancedOpen ? '접기' : '펼치기'}</span>
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-2">
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                  자동감지가 실패할 때만 사용하는 수동 방송 URL 입력란입니다. 일반적으로 비워두세요.
                </p>
                <input
                  placeholder="수동 방송 URL (예: https://youtube.com/watch?v=...)"
                  value={form.stream_url || ''}
                  onChange={(e) => setFormDirty({ stream_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                  data-testid="bcast-url"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button type="submit" disabled={saving || justSaved} className="flex-1 py-2 bg-[#FF3B30] text-white font-bold rounded disabled:opacity-70 inline-flex items-center justify-center gap-1" data-testid="bcast-save">
            {justSaved ? (<><Check size={14} /> 저장됨</>) : saving ? '저장 중...' : '저장'}
          </button>
          <button type="button" onClick={requestClose} className="px-4 py-2 border border-black/10 dark:border-white/10 rounded font-bold">취소</button>
        </div>
      </form>
    </div>
  );
};

const BroadcastsSection = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);
  const [recFor, setRecFor] = useState(null);

  // Derived from server response — never hardcoded.
  const productCounts = useMemo(() => {
    const map = {};
    (items || []).forEach((b) => { map[b.schedule_id] = b.linked_product_count || 0; });
    return map;
  }, [items]);

  const load = async () => {
    const r = await api.get('/seller/broadcasts');
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('방송을 삭제하시겠습니까?')) return;
    await api.delete(`/seller/broadcasts/${id}`);
    load();
  };
  const cancelBroadcast = async (b) => {
    if (b.status === '취소') return;
    if (!window.confirm(`"${b.title}" 방송을 취소 상태로 변경하시겠습니까?\n(삭제는 아니며 나중에 다시 편집할 수 있습니다.)`)) return;
    try {
      await api.put(`/seller/broadcasts/${b.schedule_id}`, { status: '취소' });
      toast.success('방송이 취소되었습니다.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '취소에 실패했습니다.');
    }
  };
  const dup = async (id) => {
    await api.post(`/seller/broadcasts/${id}/duplicate`);
    load();
  };
  const togglePublic = async (b) => {
    await api.put(`/seller/broadcasts/${b.schedule_id}`, { is_public: !b.is_public });
    await load();
  };
  const toggleAutoDetectTarget = async (b) => {
    const next = !b.is_yt_autodetect_target;
    try {
      await api.post(`/seller/broadcasts/${b.schedule_id}/youtube/auto-detect-target`, { enabled: next });
      toast.success(next
        ? '이 방송이 다음 YouTube LIVE 와 자동 연결됩니다.'
        : '자동 연결 대상에서 해제되었습니다.');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(msg || '현재 LIVE 진행 중에는 변경할 수 없습니다.');
      } else {
        toast.error(msg || '설정에 실패했습니다.');
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>방송 관리</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full" data-testid="add-broadcast">
          <Plus size={14} /> 방송 등록
        </button>
      </div>
      <UnlinkedLiveVideos />
      {items.length === 0 ? (
        <EmptyState title="아직 등록된 방송이 없습니다." subtitle="첫 방송을 등록해보세요." testId="broadcasts-empty" />
      ) : (
        <div className="space-y-2" data-testid="broadcasts-list">
          {items.map((b) => (
            <Card key={b.schedule_id} className="p-4 flex flex-wrap sm:flex-nowrap items-center gap-3" >
              <SafeImg src={b.thumbnail} kind="broadcast" alt="" className="w-14 h-14 rounded object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-sm truncate">{b.title}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{b.status}</span>
                  {b.is_public === false && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">비공개</span>}
                  {b.recording_enabled && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 font-bold" data-testid={`rec-badge-${b.schedule_id}`}>
                      녹화 ON · {b.recording_mode === 'clean_participant' ? '클린' : '방송화면'}
                    </span>
                  )}
                  {b.is_yt_autodetect_target && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 font-bold" data-testid={`autodetect-target-badge-${b.schedule_id}`}>
                      <Target size={10} /> 자동감지 대상
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {format(new Date(b.scheduled_at), 'M월 d일 HH:mm', { locale: ko })} · {b.platform} · {b.category}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1 w-full sm:w-auto">
                {b.stream_url && (
                  <a href={b.stream_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10" title="외부 링크 열기" data-testid={`open-broadcast-${b.schedule_id}`}>
                    <ExternalLink size={14} />
                  </a>
                )}
                <button onClick={() => togglePublic(b)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs" title="공개/비공개 전환" data-testid={`toggle-public-${b.schedule_id}`}>
                  {b.is_public === false ? '공개' : '비공개'}
                </button>
                <button onClick={() => setPickerFor(b)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 text-xs inline-flex items-center gap-1" title="방송 판매 상품 선택" data-testid={`link-products-${b.schedule_id}`}>
                  <ShoppingBag size={14} />
                  <span className="text-[11px] font-bold">{productCounts[b.schedule_id] || 0}</span>
                </button>
                <button onClick={() => setRecFor(b)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 text-[11px] font-bold inline-flex items-center gap-1" title="녹화 · 상품영상 · 소비자 화면 테스트" data-testid={`rec-open-${b.schedule_id}`}>
                  <Film size={14} /> <span className="hidden sm:inline">녹화·영상</span>
                </button>
                <button
                  onClick={() => toggleAutoDetectTarget(b)}
                  className={`p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 inline-flex items-center gap-1 ${b.is_yt_autodetect_target ? 'text-emerald-600' : 'text-gray-400'}`}
                  title={b.is_yt_autodetect_target
                    ? '이 방송이 다음 YouTube LIVE 와 자동 연결됩니다. 클릭하면 해제됩니다.'
                    : '이 방송을 다음 YouTube LIVE 자동 연결 대상으로 지정합니다. (판매자당 1개)'}
                  data-testid={`autodetect-target-toggle-${b.schedule_id}`}
                >
                  <Target size={14} />
                </button>
                <button onClick={() => { setEditing(b); setShowForm(true); }} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid={`edit-broadcast-${b.schedule_id}`}><Edit2 size={14} /></button>
                <button onClick={() => dup(b.schedule_id)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid={`duplicate-broadcast-${b.schedule_id}`}><Copy size={14} /></button>
                {b.status !== '취소' && b.status !== '종료' && b.status !== 'ended' && (
                  <button onClick={() => cancelBroadcast(b)} className="px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/10 text-[11px] font-bold text-gray-500" title="방송 취소 (삭제 아님)" data-testid={`cancel-broadcast-${b.schedule_id}`}>취소</button>
                )}
                <button onClick={() => del(b.schedule_id)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 text-[#FF3B30]" data-testid={`delete-broadcast-${b.schedule_id}`}><Trash2 size={14} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showForm && <BroadcastForm initial={editing || undefined} onClose={() => setShowForm(false)} onSaved={load} />}
      {recFor && <BroadcastRecordingModal broadcast={recFor} onClose={() => setRecFor(null)} onSaved={load} />}
      {pickerFor && (
        <BroadcastProductPicker
          broadcastId={pickerFor.schedule_id}
          onClose={() => setPickerFor(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

// ============= PRODUCTS =============
const SHIPPING_OPTIONS = [
  { value: 'seller_default', label: '판매자 배송설정 사용' },
  { value: 'free',           label: '무료배송' },
  { value: 'custom',         label: '별도배송 (개별 배송비)' },
];

/** Option matrix editor — the value list is stored as [{name, values:[{label,extra_price,stock}]}]. */
// STEP OVERLAY-SIZE-TABLE-1 · 상품 상세 사이즈표 Editor.
// 기존 ProductOptionsEditor (구매 옵션) 와는 독립 — Variant/SKU/option_snapshot 미접촉.
// 판매자가 columns/rows 를 자유롭게 편집할 수 있는 표.
//   value = { columns: string[], rows: string[][], guide_text: string }
//   onChange(next) 호출로 상위 form state 에 반영.
const MAX_SIZE_TABLE_COLS = 10;
const MAX_SIZE_TABLE_ROWS = 20;
const SizeTableEditor = ({ value, onChange }) => {
  const v = value && typeof value === 'object' ? value : { columns: [], rows: [], guide_text: '' };
  const columns = Array.isArray(v.columns) ? v.columns : [];
  const rows = Array.isArray(v.rows) ? v.rows : [];
  const guide = typeof v.guide_text === 'string' ? v.guide_text : '';

  const material = typeof v.material === 'string' ? v.material : '';
  const etc = typeof v.etc === 'string' ? v.etc : '';
  const emit = (next) => onChange?.({
    columns: next.columns || [],
    rows: next.rows || [],
    guide_text: typeof next.guide_text === 'string' ? next.guide_text : guide,
    material: typeof next.material === 'string' ? next.material : material,
    etc: typeof next.etc === 'string' ? next.etc : etc,
  });

  const setCol = (ci, label) => {
    const nc = [...columns]; nc[ci] = label;
    emit({ columns: nc, rows });
  };
  const addCol = () => {
    if (columns.length >= MAX_SIZE_TABLE_COLS) return;
    emit({ columns: [...columns, ''], rows: rows.map((r) => [...r, '']) });
  };
  const removeCol = (ci) => {
    if (columns.length <= 1) return;
    emit({
      columns: columns.filter((_, i) => i !== ci),
      rows: rows.map((r) => r.filter((_, i) => i !== ci)),
    });
  };
  const setCell = (ri, ci, val) => {
    const nr = rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? val : c)) : r));
    emit({ columns, rows: nr });
  };
  const addRow = () => {
    if (rows.length >= MAX_SIZE_TABLE_ROWS) return;
    emit({ columns, rows: [...rows, Array(columns.length || 1).fill('')] });
  };
  const removeRow = (ri) => {
    emit({ columns, rows: rows.filter((_, i) => i !== ri) });
  };
  const seedDefault = () => {
    emit({
      columns: ['사이즈', '총장', '어깨', '가슴', '소매'],
      rows: [['S', '', '', '', ''], ['M', '', '', '', ''], ['L', '', '', '', '']],
    });
  };

  const isEmpty = columns.length === 0 && rows.length === 0;
  return (
    <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid="size-table-editor">
      {isEmpty ? (
        <div className="py-3 text-center">
          <p className="text-[11px] text-gray-400 mb-2">아직 사이즈표가 없어요. 자주 쓰는 의류 사이즈 양식으로 시작하거나, 직접 항목을 추가하세요.</p>
          <div className="flex items-center justify-center gap-2">
            <button type="button" onClick={seedDefault}
                    className="px-3 py-1.5 rounded-full text-[11px] font-black border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
                    data-testid="size-table-seed-default">+ 기본 사이즈표 사용 (사이즈/총장/어깨/가슴/소매)</button>
            <button type="button" onClick={() => emit({ columns: ['사이즈'], rows: [['']] })}
                    className="px-3 py-1.5 rounded-full text-[11px] font-black border border-dashed border-black/15 dark:border-white/15 hover:bg-black/5"
                    data-testid="size-table-start-blank">+ 빈 표로 시작</button>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="size-table-grid">
            <thead>
              <tr className="border-b border-black/10 dark:border-white/10">
                {columns.map((c, ci) => (
                  <th key={ci} className="p-1 min-w-[80px]">
                    <div className="flex items-center gap-1">
                      <input value={c} onChange={(e) => setCol(ci, e.target.value)}
                             placeholder={ci === 0 ? '사이즈' : '항목'}
                             className="w-full px-2 py-1 border rounded border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs font-black"
                             data-testid={`size-table-col-${ci}`} />
                      {columns.length > 1 && (
                        <button type="button" onClick={() => removeCol(ci)}
                                className="p-1 text-gray-400 hover:text-[#FF3B30]"
                                aria-label="항목 삭제"
                                data-testid={`size-table-col-remove-${ci}`}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                <th className="p-1 w-10 text-center">
                  <button type="button" onClick={addCol}
                          disabled={columns.length >= MAX_SIZE_TABLE_COLS}
                          className="p-1 text-gray-500 hover:text-[#FF3B30] disabled:opacity-30"
                          aria-label="항목 추가"
                          data-testid="size-table-col-add">
                    <Plus size={14} />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-b border-black/5 dark:border-white/5">
                  {columns.map((_, ci) => (
                    <td key={ci} className="p-1">
                      <input value={(r && r[ci]) || ''} onChange={(e) => setCell(ri, ci, e.target.value)}
                             placeholder={ci === 0 ? 'S' : '-'}
                             className="w-full px-2 py-1 border rounded border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs text-center"
                             data-testid={`size-table-cell-${ri}-${ci}`} />
                    </td>
                  ))}
                  <td className="p-1 text-center">
                    <button type="button" onClick={() => removeRow(ri)}
                            className="p-1 text-gray-400 hover:text-[#FF3B30]"
                            aria-label="사이즈 행 삭제"
                            data-testid={`size-table-row-remove-${ri}`}>
                      <X size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={columns.length + 1} className="p-1 text-center">
                  <button type="button" onClick={addRow}
                          disabled={rows.length >= MAX_SIZE_TABLE_ROWS}
                          className="px-3 py-1 rounded-full text-[11px] font-black border border-dashed border-black/15 dark:border-white/15 hover:bg-black/5 disabled:opacity-30"
                          data-testid="size-table-row-add">
                    + 사이즈 행 추가
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {/* STUDIO-OVERLAY-UI-CONSOLIDATION-1 · 상품설명 소재·기타 (선택 · 입력한 항목만 LIVE 표시). */}
      {[['material', '소재', material, '예: 면 80%, 폴리 20%'], ['etc', '기타', etc, '예: 신축성 좋음 / 비침 없음']].map(([k, label, val, ph]) => (
        <label key={k} className="flex items-center gap-2">
          <span className="w-10 shrink-0 text-[11px] font-black text-gray-500">{label}</span>
          <input value={val} maxLength={200} placeholder={ph}
                 onChange={(e) => emit({ columns, rows, [k]: e.target.value })}
                 className="flex-1 px-3 py-1.5 border rounded border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs"
                 data-testid={`size-table-${k}`} />
        </label>
      ))}
      <div>
        <p className="text-[11px] text-gray-500 mb-1">착용/사이즈 안내 (선택)</p>
        <textarea value={guide} onChange={(e) => emit({ columns, rows, guide_text: e.target.value })}
                  rows={2}
                  maxLength={500}
                  placeholder="예: 모델 165cm / M 착용, 측정 방법에 따라 1~2cm 오차가 있을 수 있습니다."
                  className="w-full px-3 py-2 border rounded border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] text-xs"
                  data-testid="size-table-guide-text" />
      </div>
    </div>
  );
};


const ProductOptionsEditor = ({ value, onChange }) => {
  const options = Array.isArray(value) ? value : [];
  const setOpts = (next) => onChange(next);
  const addGroup = () => setOpts([...options, { name: '', values: [{ label: '', extra_price: 0, stock: 0, sku: '', barcode: '' }] }]);
  const removeGroup = (gi) => setOpts(options.filter((_, i) => i !== gi));
  const setGroup = (gi, patch) => setOpts(options.map((o, i) => i === gi ? { ...o, ...patch } : o));
  const setValue = (gi, vi, patch) => setOpts(options.map((o, i) => i === gi ? {
    ...o,
    values: o.values.map((v, j) => j === vi ? { ...v, ...patch } : v),
  } : o));
  const addValue = (gi) => setGroup(gi, { values: [...options[gi].values, { label: '', extra_price: 0, stock: 0, sku: '', barcode: '' }] });
  const removeValue = (gi, vi) => setGroup(gi, { values: options[gi].values.filter((_, j) => j !== vi) });

  return (
    <div className="space-y-3" data-testid="prod-options-editor">
      {options.map((g, gi) => (
        <div key={gi} className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-2">
          <div className="grid grid-cols-12 gap-2 items-center">
            <input value={g.name} onChange={(e) => setGroup(gi, { name: e.target.value })} placeholder="옵션 그룹명 (예: 색상)" className="col-span-10 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-opt-group-name-${gi}`} />
            <button type="button" onClick={() => removeGroup(gi)} className="col-span-2 text-xs text-[#FF3B30] font-bold text-right">그룹 삭제</button>
          </div>
          {/* A4-1G-2E-1 · 옵션 그룹 1개 → 각 옵션 행에 재고 입력 표시.
              옵션 2개 이상 → 재고 열 숨김 · 아래 [옵션 조합별 재고] 만 사용.
              A4-1G-2F · 단일 옵션 그룹에도 `재고 동일` 체크박스 추가 (UI-only). */}
          {options.length === 1 ? (
            <>
              <div className="grid grid-cols-12 gap-1.5 text-[10px] font-black tracking-widest uppercase text-gray-400 px-0.5 items-center">
                <span className="col-span-4">옵션명</span>
                <span className="col-span-2">추가금액</span>
                <span className="col-span-1 flex items-center gap-1">
                  재고
                  <label className="normal-case tracking-normal text-[10px] font-bold text-gray-500 inline-flex items-center gap-0.5 cursor-pointer ml-1" data-testid={`prod-opt-unified-stock-label-${gi}`}>
                    <input type="checkbox"
                      checked={!!g._unified_stock}
                      onChange={(e) => {
                        const next = e.target.checked;
                        if (next && g.values && g.values.length > 0) {
                          // ON · master = first value's stock · mirror to all values.
                          const master = Number(g.values[0]?.stock ?? 0);
                          setGroup(gi, {
                            _unified_stock: true,
                            values: g.values.map((v) => ({ ...v, stock: master })),
                          });
                        } else {
                          setGroup(gi, { _unified_stock: false });
                        }
                      }}
                      className="accent-[#FF3B30]"
                      data-testid={`prod-opt-unified-stock-cb-${gi}`}/>
                    <span>동일</span>
                  </label>
                </span>
                <span className="col-span-2">SKU</span>
                <span className="col-span-2">Barcode</span>
                <span className="col-span-1"></span>
              </div>
              <div className="space-y-1.5">
                {g.values.map((v, vi) => {
                  const stockReadOnly = !!g._unified_stock && vi > 0;
                  return (
                  <div key={vi} className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={v.label} onChange={(e) => setValue(gi, vi, { label: e.target.value })} placeholder="예: 아이보리" className="col-span-4 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-opt-label-${gi}-${vi}`} />
                    <div className="col-span-2 relative">
                      <input type="number" min="0" value={v.extra_price ?? 0} onChange={(e) => setValue(gi, vi, { extra_price: Number(e.target.value) })} placeholder="0" className="w-full pl-2 pr-6 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-opt-extra-${gi}-${vi}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">원</span>
                    </div>
                    <div className="col-span-1 relative">
                      <input type="number" min="0"
                        value={v.stock ?? 0}
                        onChange={(e) => {
                          const n = Math.max(0, Number(e.target.value) || 0);
                          if (g._unified_stock) {
                            // First value edit → mirror to every value.
                            setGroup(gi, {
                              values: g.values.map((vv) => ({ ...vv, stock: n })),
                            });
                          } else {
                            setValue(gi, vi, { stock: n });
                          }
                        }}
                        readOnly={stockReadOnly}
                        title={stockReadOnly ? '재고 동일이 켜져 있어 첫 옵션 재고와 동기화됩니다.' : undefined}
                        placeholder="0"
                        className={`w-full pl-1 pr-1 py-1.5 border rounded border-black/10 dark:border-white/10 text-xs ${stockReadOnly ? 'bg-black/5 dark:bg-white/10 text-gray-500' : 'bg-white dark:bg-[#0A0A0A]'}`}
                        data-testid={`prod-opt-stock-${gi}-${vi}`} />
                    </div>
                    <input value={v.sku || ''} onChange={(e) => setValue(gi, vi, { sku: e.target.value })} placeholder="비워두면 자동" title="비워두면 자동 생성됩니다. (예: LM-...)" className="col-span-2 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono" data-testid={`prod-opt-sku-${gi}-${vi}`} />
                    <input value={v.barcode || ''} onChange={(e) => setValue(gi, vi, { barcode: e.target.value })} placeholder="비워두면 자동" title="비워두면 LIVEMOA 내부 바코드가 자동 생성됩니다. (예: LM-B-...)" className="col-span-2 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono" data-testid={`prod-opt-barcode-${gi}-${vi}`} />
                    <button type="button" onClick={() => removeValue(gi, vi)} disabled={g.values.length <= 1} className="col-span-1 text-xs text-[#FF3B30] font-bold disabled:opacity-30" aria-label="옵션값 삭제">×</button>
                  </div>
                );})}
                <button type="button" onClick={() => {
                  // A4-1G-2F · 재고 동일 ON 상태에서 새 옵션값 추가 → 첫 값의 재고 자동 적용.
                  const master = (g._unified_stock && g.values && g.values.length > 0)
                    ? Number(g.values[0]?.stock ?? 0) : 0;
                  setGroup(gi, {
                    values: [...g.values, { label: '', extra_price: 0, stock: master, sku: '', barcode: '' }],
                  });
                }} className="text-[11px] font-bold text-[#FF3B30]" data-testid={`prod-add-opt-value-${gi}`}>+ 옵션값 추가</button>
              </div>
              <p className="text-[10px] text-gray-400">
                ※ <b>옵션 그룹명 · 옵션명</b>을 나중에 수정해도 SKU · 바코드 · 재고 · 매입처 · 공급가는 그대로 유지됩니다.
              </p>
            </>
          ) : (
            <>
              <div className="grid grid-cols-12 gap-1.5 text-[10px] font-black tracking-widest uppercase text-gray-400 px-0.5">
                <span className="col-span-5">옵션명</span>
                <span className="col-span-2">추가금액</span>
                <span className="col-span-2">SKU</span>
                <span className="col-span-2">Barcode</span>
                <span className="col-span-1"></span>
              </div>
              <div className="space-y-1.5">
                {g.values.map((v, vi) => (
                  <div key={vi} className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={v.label} onChange={(e) => setValue(gi, vi, { label: e.target.value })} placeholder="예: 아이보리" className="col-span-5 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-opt-label-${gi}-${vi}`} />
                    <div className="col-span-2 relative">
                      <input type="number" min="0" value={v.extra_price ?? 0} onChange={(e) => setValue(gi, vi, { extra_price: Number(e.target.value) })} placeholder="0" className="w-full pl-2 pr-6 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-opt-extra-${gi}-${vi}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">원</span>
                    </div>
                    <input value={v.sku || ''} onChange={(e) => setValue(gi, vi, { sku: e.target.value })} placeholder="비워두면 자동" title="비워두면 자동 생성됩니다. (예: LM-...)" className="col-span-2 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono" data-testid={`prod-opt-sku-${gi}-${vi}`} />
                    <input value={v.barcode || ''} onChange={(e) => setValue(gi, vi, { barcode: e.target.value })} placeholder="비워두면 자동" title="비워두면 LIVEMOA 내부 바코드가 자동 생성됩니다. (예: LM-B-...)" className="col-span-2 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs font-mono" data-testid={`prod-opt-barcode-${gi}-${vi}`} />
                    <button type="button" onClick={() => removeValue(gi, vi)} disabled={g.values.length <= 1} className="col-span-1 text-xs text-[#FF3B30] font-bold disabled:opacity-30" aria-label="옵션값 삭제">×</button>
                  </div>
                ))}
                <button type="button" onClick={() => addValue(gi)} className="text-[11px] font-bold text-[#FF3B30]" data-testid={`prod-add-opt-value-${gi}`}>+ 옵션값 추가</button>
              </div>
              <p className="text-[10px] text-gray-400">
                ※ <b>옵션 그룹명 · 옵션명</b>을 나중에 수정해도 SKU · 바코드 · 재고 · 매입처 · 공급가는 그대로 유지됩니다.
                <br/>
                ※ 실제 재고는 아래 <b>"옵션 조합별 재고"</b> 에서 입력하세요. (옵션 그룹 2개 이상일 때 자동 표시)
              </p>
            </>
          )}
        </div>
      ))}
      <button type="button" onClick={addGroup} className="w-full py-2 rounded border border-dashed border-black/20 dark:border-white/20 text-xs font-bold text-gray-500 hover:text-[#FF3B30] hover:border-[#FF3B30]" data-testid="prod-add-option-group">+ 옵션 그룹 추가 (예: 사이즈)</button>
    </div>
  );
};

// BARCODE/WMS A4-1G-1 · 상품 ↔ 매입처 매칭 UI 리라이트.
//   - 매입처 검색창은 click-driven dropdown (기존 매입처 즉시 표시 · 페이지 확장 X)
//   - 신규 매입처 등록 form 은 실사용 필드만 (MOQ/발주단위/리드타임/안전재고 제거)
//   - 각 매입처는 supplier_product_name + option-별 purchase_price 만 입력
//   - 매입처 SKU / MOQ / 발주단위 / 리드타임 / 안전재고 등 legacy 필드는 UI 에서 숨김
//     (기존 DB 필드/데이터는 백엔드 sync 에서 보존)
const SupplierMappingEditor = ({ productId, suppliers, setSuppliers, allSuppliers,
                                  refreshSuppliers, variantCombinations, canonicalVK }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [newSup, setNewSup] = useState({
    name: '', contact_name: '', phone: '', kakao_id: '',
    email: '', address: '', address_detail: '', memo: '',
  });
  const [savingNew, setSavingNew] = useState(false);
  const [dupSuggestions, setDupSuggestions] = useState([]);
  const [dupChecked, setDupChecked] = useState(false);
  const wrapRef = React.useRef(null);

  const linkedIds = new Set(suppliers.map((s) => s.supplier_id));

  const addExisting = (supplier_id) => {
    if (!supplier_id) return;
    if (linkedIds.has(supplier_id)) {
      toast.error('이미 연결된 매입처입니다.');
      return;
    }
    const sup = allSuppliers.find((x) => x.supplier_id === supplier_id);
    setSuppliers([...suppliers, {
      supplier_id,
      supplier_name: sup?.name || '',
      is_primary: suppliers.length === 0,
      supplier_product_name: '',
      variant_prices: {},
    }]);
    setSearchQ('');
    setPickerOpen(false);
  };

  const patch = (idx, p) => setSuppliers(suppliers.map((s, i) => i === idx ? { ...s, ...p } : s));
  // 옵션별 가격 입력 라인 · 옵션 2개면 조합명, 옵션 1개면 값 라벨, 옵션 없으면 "공급가"
  //   (A4-1G-1C 위해 정의 위치를 상단으로 이동 · setPrice/useEffect 에서 참조).
  const priceRows = variantCombinations.length > 0
    ? variantCombinations.map((c) => ({
        vk: canonicalVK(c.option),
        label: Object.values(c.option).join(' / '),
      }))
    : [{ vk: 'default', label: '공급가' }];
  // A4-1G-1C · 공급가 동일 체크 · UI-only state (supplier_id → boolean). 저장 구조 변경 없음.
  const [unifiedPrice, setUnifiedPrice] = useState({});
  const isUnified = (sid) => !!unifiedPrice[sid];
  const setUnified = (sid, on) => setUnifiedPrice((prev) => ({ ...prev, [sid]: !!on }));
  const setPrice = (idx, vk, val) => {
    const n = Math.max(0, Number(val) || 0);
    setSuppliers(suppliers.map((s, i) => {
      if (i !== idx) return s;
      // 공급가 동일이 ON 이면 모든 priceRows 에 동일 금액을 즉시 반영.
      if (isUnified(s.supplier_id)) {
        const nextPrices = {};
        for (const r of priceRows) nextPrices[r.vk] = n;
        return { ...s, variant_prices: nextPrices };
      }
      return { ...s, variant_prices: { ...(s.variant_prices || {}), [vk]: n } };
    }));
  };
  // priceRows (variantCombinations) 변경으로 새 옵션이 추가될 때, 공급가 동일 ON 인 supplier 는
  // 첫 옵션의 금액을 신규 옵션에도 자동 적용한다.
  useEffect(() => {
    if (!priceRows.length) return;
    let changed = false;
    const next = suppliers.map((s) => {
      if (!isUnified(s.supplier_id)) return s;
      const base = Number(s.variant_prices?.[priceRows[0].vk] ?? 0);
      const cur = s.variant_prices || {};
      let dirty = false;
      const merged = { ...cur };
      for (const r of priceRows) {
        if (Number(merged[r.vk] ?? -1) !== base) { merged[r.vk] = base; dirty = true; }
      }
      if (dirty) { changed = true; return { ...s, variant_prices: merged }; }
      return s;
    });
    if (changed) setSuppliers(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceRows.map((r) => r.vk).join('|'), Object.keys(unifiedPrice).filter((k) => unifiedPrice[k]).join('|')]);
  const remove = (idx) => {
    const next = suppliers.filter((_, i) => i !== idx);
    if (next.length && !next.some((s) => s.is_primary)) next[0].is_primary = true;
    setSuppliers(next);
  };
  const setPrimary = (idx) => setSuppliers(suppliers.map((s, i) => ({ ...s, is_primary: i === idx })));

  // Click-outside → close picker
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  const runDupCheck = async () => {
    if (!newSup.name.trim() && !newSup.phone.trim() && !newSup.email.trim()) return [];
    try {
      const { data } = await api.post('/seller/suppliers/check-duplicate', {
        name: newSup.name.trim(), phone: newSup.phone.trim(), email: newSup.email.trim(),
      });
      return data.items || [];
    } catch { return []; }
  };

  const saveNewSupplier = async (force = false) => {
    if (!newSup.name.trim()) { toast.error('매입처명을 입력해주세요.'); return; }
    if (!force && !dupChecked) {
      const dups = await runDupCheck();
      if (dups.length) { setDupSuggestions(dups); setDupChecked(true); return; }
    }
    setSavingNew(true);
    try {
      const { data } = await api.post('/seller/suppliers', {
        name: newSup.name.trim(),
        contact_name: newSup.contact_name.trim() || null,
        phone: newSup.phone.trim() || null,
        kakao_id: newSup.kakao_id.trim() || null,
        email: newSup.email.trim() || null,
        address: newSup.address.trim() || null,
        address_detail: newSup.address_detail.trim() || null,
        memo: newSup.memo.trim() || null,
      });
      await refreshSuppliers();
      // 신규 매입처는 등록 즉시 현재 상품에 연결
      const sid_ = data.supplier.supplier_id;
      const nextRow = {
        supplier_id: sid_,
        supplier_name: data.supplier.name || sid_,
        is_primary: suppliers.length === 0,
        supplier_product_name: '',
        variant_prices: {},
      };
      setSuppliers([...suppliers, nextRow]);
      setNewSup({ name: '', contact_name: '', phone: '', kakao_id: '',
        email: '', address: '', address_detail: '', memo: '' });
      setDupSuggestions([]); setDupChecked(false); setCreating(false);
      toast.success('매입처가 등록되어 연결되었습니다.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '매입처 등록에 실패했습니다.');
    } finally { setSavingNew(false); }
  };

  const q = searchQ.trim().toLowerCase();
  const filtered = q
    ? allSuppliers.filter((s) => (
        (s.name || '').toLowerCase().includes(q) ||
        (s.contact_name || '').toLowerCase().includes(q) ||
        (s.phone || '').toLowerCase().includes(q)
      )).slice(0, 30)
    : allSuppliers.slice(0, 30);

  // 옵션별 가격 입력 · 옵션 2개면 조합명, 옵션 1개면 값 라벨, 옵션 없으면 "공급가"
  //   (A4-1G-1C 로 인해 이 위치의 정의는 위로 hoist 됨 · 아래 재선언 제거)

  return (
    <div className="space-y-3" data-testid="product-supplier-editor">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500">
          매입처 매칭 <span className="text-[10px] text-gray-400">(주 매입처 1개 · 여러 매입처 가능)</span>
        </p>
        <button type="button" onClick={() => { setCreating((v) => !v); setPickerOpen(false); setDupSuggestions([]); setDupChecked(false); }}
          className="text-[11px] font-bold text-[#FF3B30] hover:underline"
          data-testid="prod-sup-toggle-new">
          {creating ? '취소' : '+ 신규 매입처 등록'}
        </button>
      </div>

      {/* Click-driven picker · 검색없이 클릭 즉시 기존 매입처 목록 표시 */}
      {!creating && (
        <div className="relative" ref={wrapRef}>
          <button type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-transparent text-xs hover:bg-black/5 dark:hover:bg-white/5"
            data-testid="prod-sup-picker-open">
            <span className="text-gray-500">매입처 검색 · 클릭하여 목록 열기 (연결할 매입처 선택)</span>
            <ChevronDown size={12} className="opacity-60" />
          </button>
          {pickerOpen && (
            <div className="absolute z-40 mt-1 w-full rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] shadow-2xl p-2"
                 data-testid="prod-sup-search-results">
              <input
                autoFocus
                value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder="매입처명 · 담당자 · 전화번호 검색"
                className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs mb-1"
                data-testid="prod-sup-search"
              />
              <div className="max-h-56 overflow-y-auto" data-testid="prod-sup-search-scroll">
                {filtered.length === 0 ? (
                  <button type="button"
                    onClick={() => { setCreating(true); setPickerOpen(false); setNewSup((n) => ({ ...n, name: searchQ })); }}
                    className="w-full px-2 py-2 text-left text-xs text-[#FF3B30] font-bold hover:bg-black/5 dark:hover:bg-white/10"
                    data-testid="prod-sup-empty-new">
                    + &quot;{searchQ || '신규 매입처'}&quot; 등록
                  </button>
                ) : filtered.map((s) => {
                  const linked = linkedIds.has(s.supplier_id);
                  return (
                    <button
                      key={s.supplier_id} type="button"
                      disabled={linked}
                      onClick={() => addExisting(s.supplier_id)}
                      className={`w-full px-2 py-1.5 text-left text-xs rounded flex items-center gap-2 ${linked ? 'opacity-50 cursor-not-allowed bg-black/5 dark:bg-white/5' : 'hover:bg-black/5 dark:hover:bg-white/10'}`}
                      data-testid={`prod-sup-opt-${s.supplier_id}`}
                    >
                      <span className="flex-1 truncate">
                        <span className="font-semibold">{s.name}</span>
                        {s.contact_name && <span className="text-gray-400"> · {s.contact_name}</span>}
                        {s.phone && <span className="text-gray-400"> · {s.phone}</span>}
                      </span>
                      {linked && <span className="text-[10px] font-bold text-gray-500 flex-shrink-0">이미 연결됨</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 신규 매입처 등록 폼 · A4-1G-1 · 실사용 필드만 */}
      {creating && (
        <div className="rounded-lg border border-dashed border-[#FF3B30]/40 p-3 space-y-2 bg-[#FF3B30]/5" data-testid="prod-sup-new-form">
          {dupSuggestions.length > 0 && (
            <div className="rounded border border-amber-400/50 bg-amber-50/70 dark:bg-amber-500/10 p-2 space-y-1.5" data-testid="prod-sup-dup-warn">
              <p className="text-[11px] font-bold text-amber-700 dark:text-amber-400">유사한 기존 매입처가 있습니다.</p>
              <div className="space-y-1">
                {dupSuggestions.map((d) => (
                  <button key={d.supplier_id} type="button" onClick={() => { addExisting(d.supplier_id); setCreating(false); setDupSuggestions([]); setDupChecked(false); }}
                    className="w-full text-left text-xs px-1.5 py-1 rounded hover:bg-amber-100 dark:hover:bg-amber-500/20" data-testid={`prod-sup-dup-pick-${d.supplier_id}`}>
                    → <span className="font-semibold">{d.name}</span>
                    {d.contact_name && <span className="text-gray-500"> · {d.contact_name}</span>}
                    {d.phone && <span className="text-gray-500"> · {d.phone}</span>}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => saveNewSupplier(true)} className="text-[11px] font-bold text-[#FF3B30] hover:underline" data-testid="prod-sup-dup-force">
                그래도 신규 등록 →
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="매입처명 *" value={newSup.name} onChange={(e) => { setNewSup({ ...newSup, name: e.target.value }); setDupChecked(false); }} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-name" />
            <input placeholder="담당자명" value={newSup.contact_name} onChange={(e) => setNewSup({ ...newSup, contact_name: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-contact" />
            <input placeholder="전화번호" value={newSup.phone} onChange={(e) => { setNewSup({ ...newSup, phone: e.target.value }); setDupChecked(false); }} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-phone" />
            <input placeholder="카카오톡 연락처/ID" value={newSup.kakao_id} onChange={(e) => setNewSup({ ...newSup, kakao_id: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-kakao" />
            <input placeholder="이메일" value={newSup.email} onChange={(e) => { setNewSup({ ...newSup, email: e.target.value }); setDupChecked(false); }} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-email" />
            <input placeholder="주소" value={newSup.address} onChange={(e) => setNewSup({ ...newSup, address: e.target.value })} className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-addr" />
            <input placeholder="상세주소" value={newSup.address_detail} onChange={(e) => setNewSup({ ...newSup, address_detail: e.target.value })} className="col-span-1 sm:col-span-2 px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-addr-detail" />
          </div>
          <textarea placeholder="메모" value={newSup.memo} onChange={(e) => setNewSup({ ...newSup, memo: e.target.value })} rows={2} className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="prod-sup-new-memo" />
          <button type="button" disabled={savingNew} onClick={() => saveNewSupplier(false)}
            className="w-full py-1.5 bg-[#FF3B30] text-white text-xs font-bold rounded disabled:opacity-60"
            data-testid="prod-sup-new-save">
            {savingNew ? '등록 중…' : '매입처 등록 후 연결'}
          </button>
        </div>
      )}

      {/* 연결된 매입처 · 각 매입처: 매입처 상품명 + 옵션별 공급가 */}
      {suppliers.length === 0 ? (
        <p className="text-[11px] text-gray-400 text-center py-3">매입처를 추가하면 발주/자동 재주문에서 사용할 수 있습니다.</p>
      ) : (
        <div className="space-y-2">
          {suppliers.map((s, i) => (
            <div key={s.supplier_id} className="rounded-lg border border-black/10 dark:border-white/10 p-2.5 space-y-2" data-testid={`prod-sup-row-${i}`}>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs font-bold cursor-pointer">
                  <input type="radio" name="primary_supplier" checked={!!s.is_primary} onChange={() => setPrimary(i)} className="accent-[#FF3B30]" data-testid={`prod-sup-primary-${i}`} />
                  <span className={s.is_primary ? 'text-[#FF3B30]' : 'text-gray-500'}>{s.is_primary ? '● 주 매입처' : '보조'}</span>
                </label>
                <span className="flex-1 truncate text-sm font-semibold" data-testid={`prod-sup-name-${i}`}>{s.supplier_name || s.supplier_id}</span>
                <button type="button" onClick={() => remove(i)} className="text-xs text-gray-400 hover:text-[#FF3B30]" aria-label="연결 해제" data-testid={`prod-sup-remove-${i}`}>연결해제</button>
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">매입처 상품명</p>
                <input value={s.supplier_product_name || ''} onChange={(e) => patch(i, { supplier_product_name: e.target.value })} placeholder="예: DDM-A-BLK123 (매입처가 부르는 상품명, 선택)" className="w-full px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid={`prod-sup-product-name-${i}`} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                    {variantCombinations.length > 0 ? '옵션별 공급가' : '공급가'}
                  </p>
                  {/* A4-1G-1C · 옵션이 2개 이상일 때만 노출 · UI-only state · 저장 구조 무변경 */}
                  {priceRows.length >= 2 && (
                    <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 cursor-pointer select-none"
                      data-testid={`prod-sup-unified-toggle-${i}`}>
                      <input type="checkbox"
                        checked={isUnified(s.supplier_id)}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setUnified(s.supplier_id, on);
                          if (on) {
                            // 즉시 첫 옵션 금액으로 나머지 옵션을 동기화.
                            const base = Number(s.variant_prices?.[priceRows[0].vk] ?? 0);
                            const nextPrices = {};
                            for (const r of priceRows) nextPrices[r.vk] = base;
                            patch(i, { variant_prices: nextPrices });
                          }
                          // 해제 시: 현재 금액 유지 (아무 것도 하지 않음).
                        }}
                        className="accent-[#FF3B30]"
                        data-testid={`prod-sup-unified-cb-${i}`} />
                      <span>공급가 동일</span>
                    </label>
                  )}
                </div>
                <div className="space-y-1">
                  {priceRows.map((row, ri) => {
                    const unified = isUnified(s.supplier_id);
                    const readOnly = unified && ri > 0;
                    return (
                    <div key={row.vk} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`prod-sup-price-row-${i}-${row.vk}`}>
                      <span className="col-span-6 text-xs truncate" title={row.label}>{row.label}</span>
                      <div className="col-span-6 relative">
                        <input type="number" min="0"
                          value={s.variant_prices?.[row.vk] ?? ''}
                          onChange={(e) => setPrice(i, row.vk, e.target.value)}
                          readOnly={readOnly}
                          title={readOnly ? '공급가 동일이 켜져 있어 첫 옵션 금액과 동기화됩니다.' : undefined}
                          placeholder="0"
                          className={`w-full pl-2 pr-6 py-1.5 border rounded border-black/10 dark:border-white/10 text-xs ${readOnly ? 'bg-black/5 dark:bg-white/10 text-gray-500' : 'bg-white dark:bg-[#0A0A0A]'}`}
                          data-testid={`prod-sup-price-${i}-${row.vk}`} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">원</span>
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductForm = ({ initial, broadcasts, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', image: '', additional_images: [], sku: '', barcode: '', brand: '',
    original_price: '', sale_price: '', description: '', detail_blocks: [],
    external_url: '', category: 'others', sale_status: '판매 중',
    connected_broadcasts: [], is_public: true, options: [],
    shipping_option: 'seller_default',
    // STEP OVERLAY-SIZE-TABLE-1 · 상품 상세 사이즈표 (실측 치수) · optional.
    // Variant/SKU 와 독립. 빈 값이면 서버가 None 처리 (field 미저장).
    size_table: { columns: [], rows: [], guide_text: '' },
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // STEP 7-INVENTORY-F · 옵션 조합별 재고 상태 (variant_key → total_stock)
  const [variantStocks, setVariantStocks] = useState({});
  // A4-1G-2E · "재고 동일" · UI-only 상태 (신규 DB 필드 없음). ON 이면 첫 조합
  //   재고를 마스터로 삼아 모든 조합의 재고를 동기화 · 새 조합 자동 상속.
  const [unifiedStock, setUnifiedStock] = useState(false);
  const isEdit = !!initial?.product_id;
  useUnsavedGuard(dirty && !saving);

  const refreshSuppliers = async () => {
    try {
      const { data } = await api.get('/seller/suppliers', { params: { active: 'true' } });
      setAllSuppliers(data.items || []);
    } catch { setAllSuppliers([]); }
  };

  useEffect(() => {
    refreshSuppliers();
    if (isEdit && initial?.product_id) {
      // A4-1G-1 · 매입처 hydrate · variant_key 무시하고 supplier 단위로 group.
      // supplier_product_name 은 첫 non-empty 값 채택 (동일 supplier 는 하나만 존재).
      // variant_prices: { <variant_key>: price } (default 포함).
      api.get('/seller/product-suppliers', { params: { product_id: initial.product_id } })
        .then(async ({ data }) => {
          const items = data.items || [];
          if (!items.length) return;
          const bySupplier = new Map();
          for (const r of items) {
            const sid_ = r.supplier_id;
            if (!bySupplier.has(sid_)) {
              bySupplier.set(sid_, {
                supplier_id: sid_,
                is_primary: false,
                supplier_product_name: '',
                variant_prices: {},
              });
            }
            const rec = bySupplier.get(sid_);
            if (r.is_primary) rec.is_primary = true;
            if (r.supplier_product_name && !rec.supplier_product_name) {
              rec.supplier_product_name = r.supplier_product_name;
            }
            rec.variant_prices[r.variant_key || 'default'] = Number(r.purchase_price ?? 0);
          }
          const { data: supData } = await api.get('/seller/suppliers', { params: { active: 'true' } });
          const nameMap = new Map((supData.items || []).map((s) => [s.supplier_id, s.name]));
          setSuppliers([...bySupplier.values()].map((rec) => ({
            ...rec,
            supplier_name: nameMap.get(rec.supplier_id) || rec.supplier_id,
          })));
        }).catch(() => {});
      // STEP 7-INVENTORY-F · 기존 variant 재고 조회 (variant_key !== 'default' 만)
      api.get('/seller/inventory').then(({ data }) => {
        const map = {};
        (data.items || []).forEach((r) => {
          if (r.product_id === initial.product_id && r.variant_key && r.variant_key !== 'default') {
            map[r.variant_key] = Number(r.total_stock ?? 0);
          }
        });
        setVariantStocks(map);
        // A4-1G-2E-1 · 옵션 그룹 1개 상품 → seller_inventory 실제 재고를
        //   options[0].values[].stock 로 hydrate.  ProductOptionsEditor 의 재고 열이
        //   기존 재고를 정확하게 보여준다.  save 시 이 값이 variant_stocks 로 전송.
        setForm((f) => {
          const opts = f.options || [];
          if (opts.length !== 1) return f;
          const g = opts[0] || {};
          const gname = (g.name || g.group_name || '').trim();
          if (!gname) return f;
          const nextVals = (g.values || []).map((v) => {
            const label = (v?.label || '').trim();
            if (!label) return v;
            const vk = `${gname}=${label}`;
            const invStock = map[vk];
            if (invStock === undefined || invStock === null) return v;
            return { ...v, stock: Number(invStock) };
          });
          return { ...f, options: [{ ...g, values: nextVals }, ...opts.slice(1)] };
        });
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormDirty = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setSuppliersDirty = (next) => { setSuppliers(next); setDirty(true); };

  // STEP 7-INVENTORY-F · 옵션 조합 계산 (최대 2그룹 · 최대 25조합)
  const variantCombinations = React.useMemo(() => {
    const opts = form.options || [];
    if (!opts.length) return [];
    const g1 = opts[0] || {};
    const n1 = (g1.name || g1.group_name || '').trim();
    if (!n1) return [];
    const v1s = (g1.values || []).filter((v) => (v?.label || '').trim());
    if (!v1s.length) return [];
    if (opts.length === 1) {
      return v1s.slice(0, 25).map((v) => {
        const label = v.label.trim();
        return { option: { [n1]: label }, display: `${n1}: ${label}` };
      });
    }
    const g2 = opts[1] || {};
    const n2 = (g2.name || g2.group_name || '').trim();
    if (!n2) {
      return v1s.slice(0, 25).map((v) => {
        const label = v.label.trim();
        return { option: { [n1]: label }, display: `${n1}: ${label}` };
      });
    }
    const v2s = (g2.values || []).filter((v) => (v?.label || '').trim());
    const out = [];
    for (const v1 of v1s) {
      for (const v2 of v2s) {
        out.push({
          option: { [n1]: v1.label.trim(), [n2]: v2.label.trim() },
          display: `${n1}: ${v1.label.trim()} · ${n2}: ${v2.label.trim()}`,
        });
        if (out.length >= 25) break;
      }
      if (out.length >= 25) break;
    }
    return out;
  }, [form.options]);

  // _variant_key 캐노니컬 (alphabetical sort · backend inventory._variant_key 와 동일)
  const canonicalVK = (opt) => {
    if (!opt || typeof opt !== 'object') return 'default';
    const keys = Object.keys(opt).sort();
    const parts = keys.filter((k) => opt[k] !== '' && opt[k] != null).map((k) => `${k}=${opt[k]}`);
    return parts.length ? parts.join('|') : 'default';
  };

  const setVariantStock = (vk, val) => {
    const n = Math.max(0, Number(val) || 0);
    setVariantStocks((cur) => ({ ...cur, [vk]: n }));
    setDirty(true);
  };
  const overCap = (form.options || []).length > 2;
  const capped = variantCombinations.length >= 25;

  const toggleBroadcast = (id) => {
    setDirty(true);
    setForm((f) => ({
      ...f,
      connected_broadcasts: f.connected_broadcasts.includes(id) ? f.connected_broadcasts.filter((x) => x !== id) : [...f.connected_broadcasts, id],
    }));
  };

  // A4-1G-1B · 대표 이미지 + 추가 이미지를 하나의 배열로 통합 조작.
  //   저장 구조는 그대로 REUSE: form.image = allImages[0], form.additional_images = allImages[1..]
  const allImages = [form.image, ...(form.additional_images || [])].filter(Boolean);
  const setAllImages = (arr) => {
    const clean = (arr || []).filter(Boolean).slice(0, 10);
    setFormDirty({ image: clean[0] || '', additional_images: clean.slice(1) });
  };
  const addAdditionalImage = (url) => {
    if (!url) return;
    if (allImages.length >= 10) return;
    setAllImages([...allImages, url]);
  };
  const removeAdditionalImage = (idx) => {
    setAllImages(allImages.filter((_, i) => i !== idx));
  };
  const setPrimaryImage = (idx) => {
    if (idx <= 0 || idx >= allImages.length) return;
    const cur = allImages.slice();
    const [pick] = cur.splice(idx, 1);
    cur.unshift(pick);
    setAllImages(cur);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    // Enforce ≤1 primary
    const primCount = suppliers.filter((s) => s.is_primary).length;
    if (suppliers.length > 0 && primCount !== 1) {
      toast.error('주 매입처를 정확히 1개 지정해주세요.');
      return;
    }
    setSaving(true);
    try {
      const toIntOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);
      // STEP 7-INVENTORY-F / A4-1G-1 · variant_stocks 페이로드.
      // · 옵션 2개 이상: variantStocks matrix 값 사용 (기존 동작)
      // · 옵션 1개    : matrix UI 를 숨겼으므로 options[0].values[].stock 로부터 유도
      //   → 판매자가 옵션값 stock 입력만으로도 seller_inventory 에 반영되도록.
      const singleGroup = (form.options || []).length === 1;
      const g0 = (form.options || [])[0] || {};
      const gName0 = (g0.name || '').trim();
      const stockFromOptions = new Map(
        singleGroup && gName0
          ? (g0.values || [])
              .filter((v) => (v?.label || '').trim())
              .map((v) => [v.label.trim(), Number(v.stock ?? 0)])
          : []
      );
      const variantStocksPayload = variantCombinations
        .map((c) => {
          const vk = canonicalVK(c.option);
          let stock;
          if (singleGroup) {
            const label = Object.values(c.option)[0];
            stock = Number(stockFromOptions.get(label) ?? 0);
          } else {
            stock = Number(variantStocks[vk] ?? 0);
          }
          return { option: c.option, total_stock: stock };
        });
      const body = {
        ...form,
        original_price: toIntOrNull(form.original_price) ?? 0,
        sale_price: toIntOrNull(form.sale_price) ?? 0,
        is_public: !!form.is_public,
        ...(variantStocksPayload.length ? { variant_stocks: variantStocksPayload } : {}),
      };
      let savedId = initial?.product_id;
      if (isEdit) {
        await api.put(`/seller/products/${initial.product_id}`, body);
      } else {
        const { data } = await api.post('/seller/products', body);
        savedId = data.product_id;
      }
      // Sync suppliers · A4-1G-1 · by_variant 모드 · 옵션별 매입가 전송.
      if (savedId) {
        const supplierRows = suppliers.map((s) => {
          let variants;
          if (variantCombinations.length > 0) {
            variants = variantCombinations.map((c) => {
              const vk = canonicalVK(c.option);
              const price = Number(s.variant_prices?.[vk] ?? s.variant_prices?.default ?? 0);
              return { variant_key: vk, purchase_price: price };
            });
          } else {
            variants = [{
              variant_key: 'default',
              purchase_price: Number(s.variant_prices?.default ?? 0),
            }];
          }
          return {
            supplier_id: s.supplier_id,
            is_primary: !!s.is_primary,
            supplier_product_name: (s.supplier_product_name || '').trim() || null,
            variants,
          };
        });
        await api.post(`/seller/products/${savedId}/suppliers`, {
          by_variant: true,
          suppliers: supplierRows,
        });
      }
      toast.success(isEdit ? '상품이 수정되었습니다.' : '상품이 등록되었습니다.');
      setJustSaved(true);
      setDirty(false);
      onSaved?.();
      setTimeout(() => { setJustSaved(false); onClose(); }, 900);
    } catch (err) {
      toast.error(err?.response?.data?.detail || '저장에 실패했습니다. 다시 시도해주세요.');
    } finally { setSaving(false); }
  };

  const requestClose = () => {
    if (dirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 페이지를 나가시겠습니까?')) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={requestClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white dark:bg-[#141414] rounded-lg max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6" data-testid="product-form">
        <h3 className="text-xl font-black tracking-tight mb-4">{isEdit ? '상품 수정' : '상품 등록'}</h3>
        <div className="space-y-3">
          <input required placeholder="상품명" value={form.name} onChange={(e) => setFormDirty({ name: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="prod-name" />
          {/* A4-1G-1 · SKU/Barcode 헬퍼 텍스트 · 자동 생성 규칙 안내 (기존 auto-gen 로직 무변경) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <input placeholder="SKU (선택)" value={form.sku || ''} onChange={(e) => setFormDirty({ sku: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="prod-sku" />
              <p className="text-[10px] text-gray-400 mt-0.5" data-testid="prod-sku-hint">비워두면 자동 생성됩니다. (예: LM-...)</p>
            </div>
            <div>
              <input placeholder="Barcode (선택)" value={form.barcode || ''} onChange={(e) => setFormDirty({ barcode: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm font-mono" data-testid="prod-barcode" />
              <p className="text-[10px] text-gray-400 mt-0.5 leading-snug" data-testid="prod-barcode-hint">
                비워두면 LIVEMOA 내부 바코드가 자동 생성됩니다. (예: LM-B-...)<br/>
                기존 상품 바코드(EAN/UPC 등)가 있으면 직접 입력하세요.
              </p>
            </div>
            <div>
              <input placeholder="브랜드 (선택)" value={form.brand || ''} onChange={(e) => setFormDirty({ brand: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="prod-brand" />
            </div>
          </div>

          {/* A4-1G-1B · 상품 이미지 통합 UI · 대표+추가 하나의 thumbnail grid ·
              대표는 첫 번째 (form.image) · 나머지는 form.additional_images · 저장 구조 그대로 REUSE.
              모든 thumbnail: 동일 크기 · object-contain · 크롭 X · 최대 10장. */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">상품 이미지</p>
            <p className="text-[10px] text-gray-400 mb-2">대표로 사용할 이미지를 선택할 수 있습니다. (최대 10장)</p>
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="prod-images-grid">
              {allImages.map((url, i) => (
                <div key={`${url}-${i}`}
                  className={`relative aspect-square rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 ${i === 0 ? 'ring-2 ring-[#FF3B30]' : 'border border-black/10 dark:border-white/10'}`}
                  data-testid={`prod-img-cell-${i}`}>
                  <SafeImg src={url} kind="product" alt="" className="w-full h-full object-contain" />
                  {i === 0 && (
                    <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-[#FF3B30] text-white text-[9px] font-black tracking-wider" data-testid={`prod-img-primary-badge`}>대표</span>
                  )}
                  {i > 0 && (
                    <button type="button" onClick={() => setPrimaryImage(i)}
                      className="absolute bottom-1 left-1 right-6 px-1 py-0.5 rounded bg-black/70 text-white text-[9px] font-bold text-center hover:bg-[#FF3B30]"
                      data-testid={`prod-img-set-primary-${i}`}>대표로 지정</button>
                  )}
                  <button type="button" onClick={() => removeAdditionalImage(i)}
                    className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 hover:bg-[#FF3B30]"
                    aria-label="이미지 삭제"
                    data-testid={`prod-img-remove-${i}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              {allImages.length < 10 && (
                <ImageUpload value="" onChange={addAdditionalImage} kind="product"
                  label={allImages.length === 0 ? '+ 이미지 추가' : '+ 이미지 추가'}
                  aspectClass="aspect-square" objectFit="contain" testId="prod-img-add" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input type="number" min="0" placeholder="정상가 (원)" value={form.original_price} onChange={(e) => setFormDirty({ original_price: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="prod-orig-price" />
            <input type="number" min="0" placeholder="판매가 (원)" value={form.sale_price} onChange={(e) => setFormDirty({ sale_price: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="prod-sale-price" />
          </div>

          <textarea placeholder="상품 요약 설명 (한 줄 소개)" value={form.description} onChange={(e) => setFormDirty({ description: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" rows={2} />

          {/* STEP 5 · 상품 상세 Editor — 텍스트/상세이미지/HTML 블록 조합 */}
          <ProductDetailEditor
            value={form.detail_blocks || []}
            onChange={(next) => setFormDirty({ detail_blocks: next })}
          />

          <div className="grid grid-cols-3 gap-2">
            <select value={form.category} onChange={(e) => setFormDirty({ category: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm">
              {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={form.sale_status} onChange={(e) => setFormDirty({ sale_status: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="prod-status">
              {PRODUCT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={form.shipping_option} onChange={(e) => setFormDirty({ shipping_option: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm" data-testid="prod-shipping-option">
              {SHIPPING_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_public !== false} onChange={(e) => setFormDirty({ is_public: e.target.checked })} className="accent-[#FF3B30]" data-testid="prod-is-public" />
            공개 상품 (판매자 프로필과 상세 페이지에 노출)
          </label>

          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1.5">상품 옵션 (색상 · 사이즈 등)</p>
            <ProductOptionsEditor value={form.options || []} onChange={(next) => setFormDirty({ options: next })} />
          </div>

          {/* STEP OVERLAY-SIZE-TABLE-1 · 상품 상세 사이즈표 (실측 치수).
              기존 '상품 옵션' (S/M/L 등 구매 옵션) 과는 완전히 다른 데이터.
              Variant/SKU/option_snapshot 과 병합되지 않는다.  선택 입력 · 비워 두면
              상품 저장은 그대로 진행되고 LIVE 사이즈표 Overlay 는 비활성화된다. */}
          <div data-testid="prod-size-table-section">
            <p className="text-xs font-semibold text-gray-500 mb-1">상품설명 · 사이즈·소재·기타 (선택)</p>
            <p className="text-[11px] text-gray-400 mb-2 leading-snug">
              상품의 실제 치수를 입력해 주세요. 라이브 상품 소개에서도 사용할 수 있습니다.
            </p>
            <SizeTableEditor
              value={form.size_table || { columns: [], rows: [], guide_text: '' }}
              onChange={(next) => setFormDirty({ size_table: next })} />
          </div>

          {/* STEP 7-INVENTORY-F / A4-1G-1 · 옵션 조합별 재고 매트릭스 (최대 2그룹, 최대 25조합) ·
              옵션 그룹이 1개일 때는 위의 [옵션명 · 재고] 입력과 중복되므로 UI 숨김 (SAVE 로직은
              options[0].values[].stock 로 자동 대체 · 백엔드 variant 저장 로직 무변경). */}
          {(form.options || []).length > 1 && (
            <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-2" data-testid="variant-stock-matrix">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[11px] font-black tracking-widest uppercase text-gray-500">옵션 조합별 재고</p>
                <div className="flex items-center gap-3">
                  {/* A4-1G-2E · 재고 동일 (공급가 동일 UX 미러) */}
                  {variantCombinations.length > 0 && (
                    <label className="inline-flex items-center gap-1 text-[11px] font-bold cursor-pointer" data-testid="variant-unified-stock-label">
                      <input type="checkbox"
                        checked={unifiedStock}
                        onChange={(e) => {
                          const next = e.target.checked;
                          setUnifiedStock(next);
                          if (next && variantCombinations.length > 0) {
                            // 첫 조합 재고를 마스터로 · 나머지 조합에 즉시 반영.
                            const firstVk = canonicalVK(variantCombinations[0].option);
                            const master = Number(variantStocks[firstVk] ?? 0);
                            const nextMap = {};
                            for (const c of variantCombinations) {
                              nextMap[canonicalVK(c.option)] = master;
                            }
                            setVariantStocks(nextMap);
                            setDirty(true);
                          }
                        }}
                        className="accent-[#FF3B30]"
                        data-testid="variant-unified-stock-cb"/>
                      <span>재고 동일</span>
                    </label>
                  )}
                  {overCap && (
                    <span className="text-[10px] font-bold text-[#FF3B30]" data-testid="variant-matrix-warn-groups">
                      최대 2개 옵션 그룹까지 매트릭스 지원 · 앞의 2개만 표시됩니다
                    </span>
                  )}
                  {!overCap && capped && (
                    <span className="text-[10px] font-bold text-[#FF3B30]" data-testid="variant-matrix-warn-cap">
                      최대 25조합까지만 저장됩니다
                    </span>
                  )}
                </div>
              </div>
              {variantCombinations.length === 0 ? (
                <p className="text-xs text-gray-400" data-testid="variant-matrix-empty">
                  옵션 그룹명과 옵션값을 입력하면 조합이 자동 생성됩니다.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {variantCombinations.map((c, i) => {
                    const vk = canonicalVK(c.option);
                    const val = variantStocks[vk];
                    const readOnly = unifiedStock && i > 0;
                    return (
                      <div key={vk} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`variant-row-${i}`}>
                        <span className="col-span-8 text-xs truncate" title={c.display}>{c.display}</span>
                        <div className="col-span-4 relative">
                          <input
                            type="number"
                            min="0"
                            value={val ?? 0}
                            onChange={(e) => {
                              const n = Math.max(0, Number(e.target.value) || 0);
                              if (unifiedStock) {
                                // 첫 조합 편집 → 전체 일괄 반영 · 나머지 편집은 readOnly 라 여기 오지 않음.
                                const nextMap = {};
                                for (const cc of variantCombinations) {
                                  nextMap[canonicalVK(cc.option)] = n;
                                }
                                setVariantStocks(nextMap);
                                setDirty(true);
                              } else {
                                setVariantStock(vk, n);
                              }
                            }}
                            readOnly={readOnly}
                            title={readOnly ? '재고 동일이 켜져 있어 첫 조합 재고와 동기화됩니다.' : undefined}
                            placeholder="0"
                            className={`w-full pl-2 pr-6 py-1.5 border rounded border-black/10 dark:border-white/10 text-xs ${readOnly ? 'bg-black/5 dark:bg-white/10 text-gray-500' : 'bg-white dark:bg-[#0A0A0A]'}`}
                            data-testid={`variant-stock-input-${i}`}
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">개</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-gray-400 leading-relaxed">
                저장 시 조합별 재고가 seller_inventory 에 반영됩니다. 이미 진행 중인 예약재고/출고재고는 보존되며, 값이 그 합보다 낮으면 자동으로 최소값으로 보정됩니다.
              </p>
            </div>
          )}

          <div className="pt-2 border-t border-black/5 dark:border-white/5">
            <SupplierMappingEditor
              productId={initial?.product_id}
              suppliers={suppliers}
              setSuppliers={setSuppliersDirty}
              allSuppliers={allSuppliers}
              refreshSuppliers={refreshSuppliers}
              variantCombinations={variantCombinations}
              canonicalVK={canonicalVK}
            />
          </div>

          {broadcasts?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">연결 방송</p>
              <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="prod-broadcasts">
                {broadcasts.map((b) => (
                  <label key={b.schedule_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.connected_broadcasts?.includes(b.schedule_id)} onChange={() => toggleBroadcast(b.schedule_id)} className="accent-[#FF3B30]" />
                    <span className="truncate">{b.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        {initial?.product_id && <ProductLiveVideos productId={initial.product_id} />}
        <div className="flex gap-2 mt-5 sticky bottom-0 pt-3 bg-white dark:bg-[#141414]">
          <button type="submit" disabled={saving} className="flex-1 py-2 bg-[#FF3B30] text-white font-bold rounded" data-testid="prod-save">{saving ? '저장 중…' : justSaved ? '저장됨' : '저장'}</button>
          <button type="button" onClick={requestClose} className="px-4 py-2 border border-black/10 dark:border-white/10 rounded font-bold">취소</button>
        </div>
      </form>
    </div>
  );
};

const STATUS_LABELS = {
  all: '전체', selling: '판매중', stopped: '판매중지',
  sold_out: '품절', low_stock: '재고부족', hidden: '비공개',
};
const STATUS_ORDER = ['all', 'selling', 'stopped', 'sold_out', 'low_stock', 'hidden'];
const SORT_OPTIONS = [
  { value: 'created_desc', label: '최신 등록순' },
  { value: 'created_asc',  label: '오래된 등록순' },
  { value: 'updated_desc', label: '최근 수정순' },
  { value: 'name_asc',     label: '상품명 가나다순' },
  { value: 'price_desc',   label: '판매가 높은순' },
  { value: 'price_asc',    label: '판매가 낮은순' },
  { value: 'stock_desc',   label: '재고 많은순' },
  { value: 'stock_asc',    label: '재고 적은순' },
];

const _fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const DATE_PRESETS = [
  { key: 'all',    label: '전체' },
  { key: 'today',  label: '오늘' },
  { key: 'yesterday', label: '어제' },
  { key: '7d',    label: '최근 7일' },
  { key: '30d',   label: '최근 30일' },
  { key: 'this_month', label: '이번 달' },
  { key: 'last_month', label: '지난 달' },
  { key: 'custom', label: '직접 선택' },
];
const computePresetRange = (key) => {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'all') return { from: '', to: '' };
  if (key === 'today') return { from: _fmtDate(startOfDay), to: _fmtDate(startOfDay) };
  if (key === 'yesterday') {
    const y = new Date(startOfDay); y.setDate(y.getDate() - 1);
    return { from: _fmtDate(y), to: _fmtDate(y) };
  }
  if (key === '7d') { const s = new Date(startOfDay); s.setDate(s.getDate() - 6); return { from: _fmtDate(s), to: _fmtDate(startOfDay) }; }
  if (key === '30d'){ const s = new Date(startOfDay); s.setDate(s.getDate() - 29); return { from: _fmtDate(s), to: _fmtDate(startOfDay) }; }
  if (key === 'this_month') {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: _fmtDate(s), to: _fmtDate(startOfDay) };
  }
  if (key === 'last_month') {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: _fmtDate(s), to: _fmtDate(e) };
  }
  return null;
};

const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const pages = [];
  const push = (n) => pages.push(n);
  const near = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
  let lastPushed = 0;
  for (let i = 1; i <= totalPages; i++) {
    if (near.has(i)) {
      if (i - lastPushed > 1) push('…');
      push(i);
      lastPushed = i;
    }
  }
  return (
    <div className="flex items-center justify-center gap-1 flex-wrap" data-testid="products-pagination">
      <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}
        className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-40"
        data-testid="pagination-prev">
        <ChevronLeft size={12} className="inline" /> 이전
      </button>
      {pages.map((n, i) => n === '…' ? (
        <span key={`e${i}`} className="px-2 text-xs text-gray-400">…</span>
      ) : (
        <button key={n} onClick={() => onChange(n)}
          className={`min-w-[32px] px-2 py-1 rounded text-xs font-bold border ${n === page ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]'}`}
          data-testid={`pagination-page-${n}`}>{n}</button>
      ))}
      <button onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}
        className="px-2.5 py-1 rounded border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-40"
        data-testid="pagination-next">
        다음 <ChevronRight size={12} className="inline" />
      </button>
    </div>
  );
};

// Phase 6.1 HOTFIX · 프로필 상품 노출 설정 (Compact) — 상품 페이지 상단으로 이동한 UI
// 재사용: GET/PATCH /seller/profile-home-config (all_products_mode)
const ProfileAllProductsModeCard = () => {
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/seller/profile-home-config')
      .then((r) => setMode(r.data?.all_products_mode || 'always'))
      .catch(() => setMode('always'));
  }, []);
  const opts = [
    { key: 'always',    label: '항상 표시', desc: '언제든 판매자 프로필에 전체상품 노출' },
    { key: 'live_only', label: 'LIVE 중만', desc: 'LIVE 방송 중일 때만 전체상품 노출' },
    { key: 'hidden',    label: '숨김',      desc: '판매자 프로필에서 전체상품 섹션 미노출' },
  ];
  const change = async (next) => {
    if (saving || next === mode) return;
    const prev = mode;
    setMode(next); setSaving(true);
    try {
      await api.patch('/seller/profile-home-config', { all_products_mode: next });
      toast.success('프로필 상품 노출 설정이 변경되었습니다.');
    } catch (e) {
      setMode(prev);
      toast.error(e?.response?.data?.detail || '저장 실패');
    } finally { setSaving(false); }
  };
  return (
    <Card className="p-3 sm:p-4" data-testid="products-profile-mode-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <p className="text-sm font-black">🛍 프로필 상품 노출 설정</p>
          <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">내 판매자 프로필에서 전체 상품목록을 언제 보여줄지 선택합니다.</p>
        </div>
        <div className="inline-flex rounded-full border border-black/10 dark:border-white/10 overflow-hidden self-start sm:self-auto shrink-0" role="radiogroup">
          {opts.map((o) => (
            <button key={o.key} type="button" onClick={() => change(o.key)} disabled={saving || mode === null}
              title={o.desc}
              className={`px-3 py-1.5 text-xs font-bold transition-colors ${mode === o.key ? 'bg-[#FF3B30] text-white' : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'} disabled:opacity-60`}
              data-testid={`products-mode-${o.key}`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
};

const ProductsSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [broadcasts, setBroadcasts] = useState([]);
  const [todayIds, setTodayIds] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  // STEP 5-PRODUCT-A · 체크 상품 일괄 숨김/재표시 (기존 is_public 필드 REUSE)
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Filters
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('created_desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const load = async () => {
    const params = {
      q: q || undefined,
      status: status !== 'all' ? status : undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort, page, limit,
    };
    const [pr, br, tr] = await Promise.all([
      api.get('/seller/products', { params }),
      api.get('/seller/broadcasts'),
      api.get('/seller/broadcasts/today').catch(() => ({ data: [] })),
    ]);
    const data = pr.data || {};
    setItems(Array.isArray(data) ? data : (data.items || []));
    setTotal(data.total ?? (Array.isArray(data) ? data.length : 0));
    setTotalPages(data.total_pages ?? 1);
    setStats(data.stats || {});
    setBroadcasts(br.data || []);
    const todays = tr.data || [];
    const ids = new Set();
    await Promise.all(todays.map(async (b) => {
      try {
        const r = await api.get(`/seller/broadcasts/${b.schedule_id}/live-products`);
        (r.data?.items || []).forEach((it) => ids.add(it.product_id));
      } catch (e) { /* ignore */ }
    }));
    setTodayIds(ids);
  };
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => { load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, dateFrom, dateTo, sort, page, limit]);

  // A4-1G-1A · URL query 에 action=new 또는 action=edit&product_id=... 이 있으면 자동으로 modal 을 연다.
  //   - SimpleProducts 최근 상품 카드 클릭 → ProductsSection 진입 즉시 해당 상품 수정 modal.
  //   - '+ 새 상품 등록' 큰 버튼 → action=new → 신규 등록 modal (기존 handler REUSE).
  //   - action=edit 은 items 로드 후 매칭. items 안에 없으면 (다른 페이지) 안내만 하고 URL 정리.
  // 열고 나면 URL 을 정리해서 새로고침 시 재-open 방지.
  const _autoOpenedRef = React.useRef({ new: false, edit: false });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    if (!action) return;
    const cleanupUrl = () => {
      params.delete('action'); params.delete('product_id');
      const qs = params.toString();
      navigate(qs ? `${location.pathname}?${qs}` : location.pathname, { replace: true });
    };
    if (action === 'new' && !_autoOpenedRef.current.new) {
      _autoOpenedRef.current.new = true;
      setEditing(null); setShowForm(true);
      cleanupUrl();
      return;
    }
    if (action === 'edit' && !_autoOpenedRef.current.edit) {
      const pid = params.get('product_id');
      if (!pid) { cleanupUrl(); return; }
      // items 아직 로드 전이면 대기 (다음 render 에서 매칭)
      if (!items || items.length === 0) return;
      const found = items.find((p) => p.product_id === pid);
      _autoOpenedRef.current.edit = true;
      if (found) {
        setEditing(found); setShowForm(true);
      } else {
        // 다른 페이지에 있는 상품 → 안전하게 전체 목록만 열어둔다 (사용자가 직접 검색/찾기)
        toast.info('전체 상품 목록에서 해당 상품을 찾아 수정하세요.');
      }
      cleanupUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, items]);

  const applyPreset = (key) => {
    setDatePreset(key);
    if (key === 'custom') return;  // keep existing custom dates
    const r = computePresetRange(key);
    if (r) { setDateFrom(r.from); setDateTo(r.to); setPage(1); }
  };

  const addToTodayBroadcast = async (productId) => {
    try {
      const tr = await api.get('/seller/broadcasts/today');
      const todays = tr.data || [];
      if (todays.length === 0) {
        toast.error('오늘 예정된 방송이 없습니다. 방송을 먼저 등록하세요.');
        return;
      }
      let target = todays[0];
      if (todays.length > 1) {
        const pick = window.prompt(`오늘 방송이 ${todays.length}건 있습니다.\n등록할 방송 번호를 입력하세요:\n${todays.map((b, i) => `${i + 1}. ${b.title}`).join('\n')}`, '1');
        const idx = parseInt(pick, 10);
        if (!Number.isFinite(idx) || idx < 1 || idx > todays.length) return;
        target = todays[idx - 1];
      }
      const r = await api.post(`/seller/broadcasts/${target.schedule_id}/live-products`, { product_id: productId });
      if (r.data?.already_linked) toast.info('이미 오늘 방송에 등록된 상품입니다.');
      else toast.success(`"${target.title}"에 등록되었습니다.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '등록 실패'); }
  };

  const del = async (id) => {
    if (!window.confirm('상품을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/seller/products/${id}`);
      toast.success('상품이 삭제되었습니다.'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || '삭제 실패'); }
  };
  const dup = async (id) => {
    try {
      await api.post(`/seller/products/${id}/duplicate`);
      toast.success('상품이 복제되었습니다.'); load();
    } catch (err) { toast.error(err?.response?.data?.detail || '복제 실패'); }
  };
  const reorder = async (id, delta) => {
    const idx = items.findIndex((p) => p.product_id === id);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const aItem = items[idx];
    const bItem = items[target];
    // STEP 5-PRODUCT-A REOPEN · display_order 값을 timestamp(ms) 크기로 부여 (신규 API 없음).
    // 기존 legacy 소수 display_order(1~5) 는 무시하고 created_at ms 로 초기화.
    const _MANUAL_THRESH = 1e12;
    const tsA = new Date(aItem.created_at || 0).getTime();
    const tsB = new Date(bItem.created_at || 0).getTime();
    const aOrder = (typeof aItem.display_order === 'number' && aItem.display_order > _MANUAL_THRESH) ? aItem.display_order : tsA;
    const bOrder = (typeof bItem.display_order === 'number' && bItem.display_order > _MANUAL_THRESH) ? bItem.display_order : tsB;
    // Optimistic swap for immediate visual feedback
    setItems((prev) => {
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      next[idx].display_order = bOrder;
      next[target].display_order = aOrder;
      return next;
    });
    try {
      await Promise.all([
        api.put(`/seller/products/${aItem.product_id}`, { display_order: bOrder }),
        api.put(`/seller/products/${bItem.product_id}`, { display_order: aOrder }),
      ]);
    } catch (e) {
      // rollback on failure
      setItems((prev) => {
        const next = [...prev];
        [next[idx], next[target]] = [next[target], next[idx]];
        next[idx].display_order = aItem.display_order;
        next[target].display_order = bItem.display_order;
        return next;
      });
      toast.error(e?.response?.data?.detail || '순서 저장 실패');
    }
  };

  // STEP 5-PRODUCT-A · 체크 선택/일괄 숨김·재표시 (기존 PUT /seller/products/{id} REUSE)
  const toggleSel = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const selectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      items.forEach((p) => next.add(p.product_id));
      return next;
    });
  };
  const clearSel = () => setSelected(new Set());
  const bulkSetPublic = async (nextPublic) => {
    if (selected.size === 0 || bulkBusy) return;
    const label = nextPublic ? '다시 표시' : '숨김';
    if (!window.confirm(`선택한 ${selected.size}개 상품을 ${label} 처리할까요?`)) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((pid) => api.put(`/seller/products/${pid}`, { is_public: nextPublic })),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = ids.length - ok;
    if (fail > 0) toast.error(`${ok}개 성공 · ${fail}개 실패`);
    else toast.success(`선택 ${ok}개 상품 ${label} 완료`);
    setBulkBusy(false);
    clearSel();
    load();
  };

  const rangeStart = (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);
  const hasActiveFilter = q || status !== 'all' || dateFrom || dateTo;

  const FilterPanel = () => (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5" data-testid="date-presets">
        {DATE_PRESETS.map((d) => (
          <button key={d.key} onClick={() => applyPreset(d.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${datePreset === d.key ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`}
            data-testid={`date-preset-${d.key}`}>
            {d.label}
          </button>
        ))}
      </div>
      {datePreset === 'custom' && (
        <div className="flex items-center gap-2" data-testid="date-custom">
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="px-2 py-1 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="date-from" />
          <span className="text-xs">~</span>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="px-2 py-1 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs" data-testid="date-to" />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="status-filters">
        {STATUS_ORDER.map((s) => {
          const count = s === 'all' ? (stats.all ?? total) : (stats[s] ?? 0);
          return (
            <button key={s} onClick={() => { setStatus(s); setPage(1); }}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${status === s ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`}
              data-testid={`status-${s}`}>
              {STATUS_LABELS[s]} <span className="opacity-70">{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>상품 관리</h2>
          <p className="text-xs text-gray-500 mt-0.5" data-testid="products-total-count">
            {hasActiveFilter ? '검색 결과' : '전체 상품'} <span className="font-bold text-[#FF3B30]">{total.toLocaleString()}</span>개
            {total > 0 && <span className="text-gray-400"> · {rangeStart}-{rangeEnd} / {total.toLocaleString()}</span>}
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full" data-testid="add-product">
          <Plus size={14} /> 상품 등록
        </button>
      </div>

      {/* Phase 6.1 HOTFIX · 프로필 상품 노출 설정 (상품 페이지로 이동) */}
      <ProfileAllProductsModeCard />

      {/* Search + mobile filter toggle */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="상품명 · SKU · 브랜드 · 매입처명 검색"
            className="w-full pl-8 pr-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
            data-testid="products-search" />
        </div>
        <button onClick={() => setMobileFilterOpen((v) => !v)}
          className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 border border-black/10 dark:border-white/10 rounded text-xs font-bold"
          data-testid="mobile-filter-toggle">
          <Filter size={13} /> 필터
        </button>
      </div>

      {/* Desktop filters */}
      <div className="hidden md:block">
        <FilterPanel />
      </div>

      {/* Mobile bottom sheet-ish */}
      {mobileFilterOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileFilterOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white dark:bg-[#141414] p-4 space-y-3 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-filter-sheet">
            <div className="flex items-center justify-between">
              <p className="text-sm font-black">필터</p>
              <button onClick={() => setMobileFilterOpen(false)} className="text-xs text-gray-500">닫기</button>
            </div>
            <FilterPanel />
          </div>
        </div>
      )}

      {/* Sort + page size */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
          className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
          data-testid="products-sort">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
          className="px-2 py-1.5 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-xs"
          data-testid="products-page-size">
          <option value={20}>20개씩</option>
          <option value={50}>50개씩</option>
          <option value={100}>100개씩</option>
        </select>
        {items.length > 0 && (
          <button type="button" onClick={selectAllOnPage}
            className="ml-auto text-[11px] font-bold text-gray-500 hover:text-black dark:hover:text-white"
            data-testid="products-select-all">
            이 페이지 전체 선택
          </button>
        )}
      </div>

      {/* STEP 5-PRODUCT-A · 체크 상품 일괄 액션바 (1개 이상 체크되면 노출) */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 -mx-2 sm:mx-0 px-3 py-2 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/[0.06] backdrop-blur flex items-center gap-2 flex-wrap"
             data-testid="products-bulk-bar">
          <span className="text-xs font-black" data-testid="products-bulk-count">선택 <span className="text-[#FF3B30]">{selected.size}</span>개</span>
          <span className="text-gray-300 dark:text-white/20">|</span>
          <button type="button" onClick={() => bulkSetPublic(false)} disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full text-[11px] font-black bg-black text-white hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="products-bulk-hide">
            {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <EyeOff size={12}/>} 선택 상품 숨김
          </button>
          <button type="button" onClick={() => bulkSetPublic(true)} disabled={bulkBusy}
            className="px-3 py-1.5 rounded-full text-[11px] font-black border border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="products-bulk-show">
            {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <Eye size={12}/>} 선택 상품 다시 표시
          </button>
          <button type="button" onClick={clearSel} disabled={bulkBusy}
            className="ml-auto text-[11px] font-bold text-gray-500 hover:text-black dark:hover:text-white disabled:opacity-50"
            data-testid="products-bulk-clear">
            선택 해제
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title={hasActiveFilter ? '조건에 맞는 상품이 없습니다.' : '아직 등록된 상품이 없습니다.'} subtitle={hasActiveFilter ? '검색/필터를 조정해보세요.' : '상품을 등록해보세요.'} testId="products-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="products-list">
          {items.map((p, idx) => (
            <Card key={p.product_id} className={`p-4 flex items-center gap-3 ${selected.has(p.product_id) ? 'ring-2 ring-[#FF3B30]/40' : ''}`}>
              {/* STEP 5-PRODUCT-A · 체크박스 */}
              <label className="flex items-center flex-shrink-0 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={selected.has(p.product_id)} onChange={() => toggleSel(p.product_id)}
                  className="w-4 h-4 accent-[#FF3B30]"
                  data-testid={`product-select-${p.product_id}`} />
              </label>
              {/* A4-1G-1A · 상품관리 카드 thumbnail · object-contain 로 원본 전체 표시 (bg 로 letterbox) */}
              <SafeImg src={p.image} kind="product" alt="" className="w-14 h-14 rounded object-contain bg-black/5 dark:bg-white/5" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{p.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                  <span>{p.sale_status}</span><span>·</span>
                  {p.discount_rate > 0 && <span className="text-[#FF3B30] font-bold">-{p.discount_rate}%</span>}
                  <span className="font-bold">{won(p.sale_price)}</span>
                  {p.derived_status === 'low_stock' && <span className="text-amber-600 font-bold">· 재고부족</span>}
                  {p.derived_status === 'sold_out' && <span className="text-red-500 font-bold">· 품절</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => addToTodayBroadcast(p.product_id)}
                  className={`px-2 py-1 rounded-full text-[11px] font-bold border inline-flex items-center gap-1 ${todayIds.has(p.product_id) ? 'bg-[#FF3B30]/10 border-[#FF3B30] text-[#FF3B30]' : 'border-black/10 dark:border-white/10 hover:border-[#FF3B30] hover:text-[#FF3B30]'}`}
                  title={todayIds.has(p.product_id) ? '이미 오늘 방송에 등록됨' : '오늘 방송에 등록'}
                  data-testid={`add-to-today-${p.product_id}`}>
                  <Radio size={11} /> {todayIds.has(p.product_id) ? '오늘 방송' : '오늘 방송 등록'}
                </button>
                <button onClick={() => reorder(p.product_id, -1)} disabled={idx === 0 || sort !== 'created_desc'} className="p-1 text-xs disabled:opacity-30" title={sort !== 'created_desc' ? '순서 이동은 최신 등록순에서만 사용 가능' : '위로'} data-testid={`prod-up-${p.product_id}`}>▲</button>
                <button onClick={() => reorder(p.product_id, +1)} disabled={idx === items.length - 1 || sort !== 'created_desc'} className="p-1 text-xs disabled:opacity-30" title={sort !== 'created_desc' ? '순서 이동은 최신 등록순에서만 사용 가능' : '아래로'} data-testid={`prod-down-${p.product_id}`}>▼</button>
                <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid={`edit-product-${p.product_id}`}><Edit2 size={14} /></button>
                <button onClick={() => dup(p.product_id)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid={`duplicate-product-${p.product_id}`}><Copy size={14} /></button>
                <button onClick={() => del(p.product_id)} className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/10 text-[#FF3B30]" data-testid={`delete-product-${p.product_id}`}><Trash2 size={14} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {showForm && <ProductForm initial={editing || undefined} broadcasts={broadcasts} onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
};

// ============= FOLLOWERS =============
const FollowersSection = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/seller/followers/summary').then((r) => setData(r.data)); }, []);
  if (!data) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>팔로워·알림</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="총 팔로워 수"       value={data.total_followers?.toLocaleString()} testId="followers-total" />
        <StatCard label="이번 주 신규"        value={data.week_new}  testId="followers-week" />
        <StatCard label="이번 달 신규"        value={data.month_new} testId="followers-month" />
        <StatCard label="알림 수신 동의자 수" value={data.notification_consenters} testId="followers-consent" />
        <StatCard label="카카오 알림 가능 (추정치)"    value={data.kakao_targets?.toLocaleString()} testId="followers-kakao" />
        <StatCard label="앱 푸시 가능"        value={data.push_targets?.toLocaleString()}  testId="followers-push" />
        <StatCard label="이메일 알림 가능"    value={data.email_targets?.toLocaleString()} testId="followers-email" />
      </div>
      <Card className="p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold">개인정보 보호</p>
          <p className="text-xs text-gray-500 mt-1">
            판매자는 소비자의 이메일 주소, 전화번호 등 개인정보를 직접 조회하거나 내보낼 수 없습니다.
            모든 알림은 LIVEMOA 시스템을 통해 발송됩니다.
          </p>
        </div>
      </Card>
    </div>
  );
};

// Prod-S5-D · TOP 숏클립 리스트 (all-time counter · click_count DESC · limit 5)
// 각 row 클릭 시 인라인 확장으로 연결 상품별 성과 상세 (S5-A drill-down).
const ShortsTopClipsList = ({ clips }) => {
  const [openId, setOpenId] = useState(null);
  const platBadge = (p) => {
    const map = { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', upload: '직접등록' };
    const cls = { youtube: 'bg-red-600', tiktok: 'bg-black', instagram: 'bg-fuchsia-600', upload: 'bg-gray-500' }[p] || 'bg-gray-500';
    return <span className={`px-1.5 py-0.5 rounded text-[9px] font-black text-white ${cls}`}>{map[p] || p}</span>;
  };
  return (
    <div className="mt-4" data-testid="an-shorts-top-clips">
      <p className="text-xs font-black text-gray-500 mb-2">TOP 숏클립 (누적)</p>
      <div className="rounded-lg border border-black/5 dark:border-white/10 overflow-hidden">
        {clips.map((c) => {
          const open = openId === c.clip_id;
          return (
            <div key={c.clip_id} className="border-b border-black/5 dark:border-white/5 last:border-b-0" data-testid={`an-shorts-top-clip-${c.clip_id}`}>
              <button type="button" onClick={() => setOpenId(open ? null : c.clip_id)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.03]">
                <div className="w-10 h-14 rounded bg-gray-100 dark:bg-white/5 overflow-hidden flex-shrink-0">
                  {c.thumbnail_url && <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {platBadge(c.platform)}
                    <p className="text-xs font-bold truncate">{c.title || '(제목 없음)'}</p>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5 truncate">{c.clip_id}</p>
                </div>
                <div className="hidden sm:grid grid-cols-5 gap-3 text-right text-[11px] font-black tracking-tight flex-shrink-0">
                  <div><p className="text-[9px] text-gray-400 font-medium">노출</p><p>{c.impression_count}</p></div>
                  <div><p className="text-[9px] text-gray-400 font-medium">클릭</p><p>{c.click_count}</p></div>
                  <div><p className="text-[9px] text-gray-400 font-medium">구매</p><p>{c.buy_click_count}</p></div>
                  <div><p className="text-[9px] text-gray-400 font-medium">주문</p><p>{c.attributed_orders}</p></div>
                  <div><p className="text-[9px] text-gray-400 font-medium">매출</p><p className="text-[#FF3B30]">{won(c.attributed_revenue)}</p></div>
                </div>
                <span className="text-gray-400 flex-shrink-0">{open ? '−' : '＋'}</span>
              </button>
              {open && (
                <div className="px-3 pb-3 pl-16 space-y-1.5" data-testid={`an-shorts-clip-detail-${c.clip_id}`}>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">연결 상품 성과</p>
                  {(c.linked_products || []).length === 0 && (
                    <p className="text-[11px] text-gray-400">연결된 상품이 없습니다.</p>
                  )}
                  {(c.linked_products || []).map((lp, i) => (
                    <div key={lp.product_id} className="flex items-center gap-2 text-[11px] py-1.5 border-t border-black/5 dark:border-white/5 first:border-t-0">
                      {i === 0 && <span className="px-1 rounded bg-[#FF3B30] text-white text-[8px] font-black">대표</span>}
                      {lp.image ? <img src={lp.image} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0"/> : <div className="w-6 h-6 rounded bg-gray-100 dark:bg-white/5 flex-shrink-0"/>}
                      <p className="flex-1 truncate font-bold">{lp.name || lp.product_id}</p>
                      <span className="text-gray-500">클릭 <b className="text-black dark:text-white">{lp.click_count}</b></span>
                      <span className="text-gray-500">구매 <b className="text-black dark:text-white">{lp.buy_click_count}</b></span>
                      <span className="text-gray-500">주문 <b className="text-black dark:text-white">{lp.attributed_orders}</b></span>
                      <span className="text-[#FF3B30] font-black">{won(lp.attributed_revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Prod-S5-D · TOP 상품 리스트 (linked_products $unwind aggregation)
const ShortsTopProductsList = ({ products }) => (
  <div className="mt-4" data-testid="an-shorts-top-products">
    <p className="text-xs font-black text-gray-500 mb-2">TOP 상품 (숏클립 유입 · 누적)</p>
    <div className="rounded-lg border border-black/5 dark:border-white/10 overflow-hidden">
      {products.map((p) => (
        <div key={p.product_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-black/5 dark:border-white/5 last:border-b-0" data-testid={`an-shorts-top-product-${p.product_id}`}>
          {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0"/> : <div className="w-10 h-10 rounded bg-gray-100 dark:bg-white/5 flex-shrink-0"/>}
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold truncate">{p.name || p.product_id}</p>
            <p className="text-[10px] text-gray-500">연결된 숏클립 {p.linked_in_clips}개</p>
          </div>
          <div className="grid grid-cols-4 gap-3 text-right text-[11px] font-black tracking-tight flex-shrink-0">
            <div><p className="text-[9px] text-gray-400 font-medium">클릭</p><p>{p.click_count}</p></div>
            <div><p className="text-[9px] text-gray-400 font-medium">구매</p><p>{p.buy_click_count}</p></div>
            <div><p className="text-[9px] text-gray-400 font-medium">주문</p><p>{p.attributed_orders}</p></div>
            <div><p className="text-[9px] text-gray-400 font-medium">매출</p><p className="text-[#FF3B30]">{won(p.attributed_revenue)}</p></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Prod-S5-D · 플랫폼별 성과 (LIVEMOA 내부 지표만 · 외부 원본 조회수 아님)
const ShortsPlatformTable = ({ platforms }) => {
  const label = { youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram', upload: '기타/직접등록' };
  return (
    <div className="mt-4" data-testid="an-shorts-platforms">
      <p className="text-xs font-black text-gray-500 mb-2">플랫폼별 성과 <span className="text-[10px] font-normal text-gray-400">(LIVEMOA 내부 지표 기준)</span></p>
      <div className="rounded-lg border border-black/5 dark:border-white/10 overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-black/[0.03] dark:bg-white/[0.03]">
            <tr className="text-left text-gray-500 font-medium">
              <th className="px-3 py-2">플랫폼</th>
              <th className="px-3 py-2 text-right">숏클립</th>
              <th className="px-3 py-2 text-right">노출</th>
              <th className="px-3 py-2 text-right">클릭</th>
              <th className="px-3 py-2 text-right">구매</th>
              <th className="px-3 py-2 text-right">주문</th>
              <th className="px-3 py-2 text-right">매출</th>
            </tr>
          </thead>
          <tbody>
            {platforms.map((p) => (
              <tr key={p.platform} className="border-t border-black/5 dark:border-white/5 font-bold" data-testid={`an-shorts-plat-${p.platform}`}>
                <td className="px-3 py-2">{label[p.platform] || p.platform}</td>
                <td className="px-3 py-2 text-right">{p.shorts_count}</td>
                <td className="px-3 py-2 text-right">{p.impression_count}</td>
                <td className="px-3 py-2 text-right">{p.click_count}</td>
                <td className="px-3 py-2 text-right">{p.buy_click_count}</td>
                <td className="px-3 py-2 text-right">{p.attributed_orders}</td>
                <td className="px-3 py-2 text-right text-[#FF3B30]">{won(p.attributed_revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============= ANALYTICS =============
const AnalyticsSection = () => {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/seller/analytics', { params: { range } }).then((r) => setData(r.data));
  }, [range]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>통계</h2>
        <div className="flex gap-2">
          {[
            { key: 'today', label: '오늘' },
            { key: '7d',    label: '7일' },
            { key: '30d',   label: '30일' },
          ].map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${range === r.key ? 'bg-[#FF3B30] text-white border-[#FF3B30]' : 'bg-white dark:bg-[#141414] border-black/10 dark:border-white/10'}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {data && !data.has_data ? (
        <EmptyState title="아직 분석할 데이터가 없습니다." subtitle="방송을 등록하면 통계가 표시됩니다." testId="analytics-empty" />
      ) : (
        <>
          <div>
            <h3 className="text-sm font-bold mb-2 text-gray-500">방송 분석</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard label="방송 노출 수"      value={data?.broadcast.impression_count}     testId="an-impr" />
              <StatCard label="방송 상세 클릭 수"  value={data?.broadcast.detail_click_count}   testId="an-detail" />
              <StatCard label="외부 방송 링크 클릭" value={data?.broadcast.external_click_count} testId="an-external" />
              <StatCard label="즐겨찾기 수"        value={data?.broadcast.favorite_count}       testId="an-fav" />
              <StatCard label="신규 팔로워 수"     value={data?.broadcast.new_follower_count}   testId="an-newfol" />
              <StatCard label="알림 신청 수"       value={data?.broadcast.notification_request_count} testId="an-notif" />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold mb-2 text-gray-500">상품 분석</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard label="상품 노출 수" value={data?.product.impression_count} testId="an-prod-impr" />
              <StatCard label="상품 클릭 수" value={data?.product.click_count}      testId="an-prod-click" />
            </div>
          </div>
          {/* Prod-S4 · 숏클립 성과 · LIVEMOA 내부 노출/클릭/주문 (외부 원본 조회수 아님) */}
          <div data-testid="an-shorts-section">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-bold text-gray-500">숏클립 성과</h3>
              <p className="text-[10px] text-gray-400">LIVEMOA 앱 내부 지표 · 원본 플랫폼 조회수와 다름</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard label="LIVEMOA 노출 (누적)" value={data?.shorts?.impression_count} testId="an-shorts-impr" />
              <StatCard label="상품목록 열기 (누적)" value={data?.shorts?.list_open_count} testId="an-shorts-listopen" />
              <StatCard label="상품 클릭 (누적)"    value={data?.shorts?.click_count}      testId="an-shorts-click" />
              <StatCard label="구매 클릭 (누적)"    value={data?.shorts?.buy_click_count}  testId="an-shorts-buy" />
              <StatCard label={`주문(유입) · ${range === 'today' ? '오늘' : range === '7d' ? '7일' : '30일'}`} value={data?.shorts?.attributed_orders} testId="an-shorts-orders" />
              <StatCard label="전환율(%)"    value={data?.shorts?.conversion_rate} testId="an-shorts-cvr" />
            </div>
            {/* Prod-S5 · 매출 attribution + 결제완료 (range 반영) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
              <StatCard label={`매출(유입) · ${range}`} value={won(data?.shorts?.attributed_revenue || 0)} testId="an-shorts-revenue" />
              <StatCard label={`결제완료 주문 · ${range}`} value={data?.shorts?.payment_completed_orders} testId="an-shorts-paid-orders" />
              <StatCard label={`결제완료 매출 · ${range}`} value={won(data?.shorts?.payment_completed_revenue || 0)} testId="an-shorts-paid-revenue" />
              <StatCard label="연결 숏클립 수" value={data?.shorts?.clip_count} testId="an-shorts-clip-count" />
            </div>

            {/* Prod-S5-D · TOP 숏클립 (all-time counter) */}
            {Array.isArray(data?.shorts?.top_clips) && data.shorts.top_clips.length > 0 && (
              <ShortsTopClipsList clips={data.shorts.top_clips} />
            )}

            {/* Prod-S5-D · TOP 상품 (all-time counter · 매출은 range 반영) */}
            {Array.isArray(data?.shorts?.top_products) && data.shorts.top_products.length > 0 && (
              <ShortsTopProductsList products={data.shorts.top_products} />
            )}

            {/* Prod-S5-D · 플랫폼별 성과 */}
            {Array.isArray(data?.shorts?.platforms) && data.shorts.platforms.length > 0 && (
              <ShortsPlatformTable platforms={data.shorts.platforms} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ============= ADS =============
const AdsSection = ({ user }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ campaign_type: AD_TYPES[0], daily_budget: 0, total_budget: 0 });
  const [sellerId, setSellerId] = useState(null);
  const load = async () => setItems((await api.get('/seller/ads')).data || []);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/seller/me');
        setSellerId(r.data?.seller_id);
      } catch { /* ignore */ }
    })();
  }, []);
  const create = async (e) => {
    e.preventDefault();
    await api.post('/seller/ads', form);
    setForm({ campaign_type: AD_TYPES[0], daily_budget: 0, total_budget: 0 });
    load();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>광고 관리</h2>

      {/* STEP GROWTH-B2 · FAN BOOST entry point (embedded, no new page). */}
      {sellerId && <FanBoostSection sellerId={sellerId} />}

      {/* STEP GROWTH-B3 · LIVE BOOST entry point (embedded, no new page). */}
      {sellerId && <LiveBoostSection sellerId={sellerId} />}

      <Card className="p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500">유료 광고 구매는 아직 활성화되지 않았습니다. 초안(draft) 상태로 캠페인을 미리 만들어 둘 수 있습니다.</p>
      </Card>
      <Card className="p-5">
        <h3 className="font-bold mb-3">캠페인 초안 만들기</h3>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="ad-form">
          <select value={form.campaign_type} onChange={(e) => setForm({ ...form, campaign_type: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10">
            {AD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="number" min="0" placeholder="일일 예산" value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
          <input type="number" min="0" placeholder="총예산"    value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
          <button type="submit" className="py-2 bg-[#FF3B30] text-white font-bold rounded" data-testid="ad-save">초안 저장</button>
        </form>
        <button disabled className="mt-3 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 text-sm font-bold rounded-full cursor-not-allowed" data-testid="ad-apply-disabled">
          광고 신청 (사용 불가 · 관리자 승인 대기)
        </button>
      </Card>
      {items.length > 0 ? (
        <Card className="divide-y divide-black/5 dark:divide-white/10" data-testid="ad-list">
          {items.map((a) => (
            <div key={a.campaign_id} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm">{a.campaign_type}</p>
                <p className="text-xs text-gray-500">일일 {won(a.daily_budget)} · 총 {won(a.total_budget)} · 사용 {won(a.spent_amount)}</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-black/5 dark:bg-white/10">{a.status}</span>
            </div>
          ))}
        </Card>
      ) : (
        <EmptyState title="아직 등록된 광고 캠페인이 없습니다." subtitle="초안을 만들어 향후 광고에 대비하세요." testId="ads-empty" />
      )}
    </div>
  );
};

// ============= WALLET =============
const WalletSection = () => {
  const [wallet, setWallet] = useState(null);
  const [prices, setPrices] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [channel, setChannel] = useState('kakao_alimtalk');

  useEffect(() => {
    api.get('/seller/wallet').then((r) => setWallet(r.data));
    api.get('/seller/service-prices').then((r) => setPrices(r.data));
  }, []);

  const runEstimate = async () => {
    const r = await api.post('/seller/notifications/estimate', { channel });
    setEstimate(r.data);
  };

  if (!wallet) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>충전금 관리</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="현재 잔액"       value={won(wallet.balance)}           testId="wallet-balance" />
        <StatCard label="사용 가능 잔액"    value={won(wallet.usable_balance)}    testId="wallet-usable" />
        <StatCard label="환불 가능 잔액"    value={won(wallet.refundable_balance)} testId="wallet-refundable" />
        <StatCard label="최근 충전 금액"    value={won(wallet.last_charge_amount)} testId="wallet-last-charge" />
        <StatCard label="이번 달 사용 금액" value={won(wallet.month_spend)}       testId="wallet-month-spend" />
      </div>
      <Card className="p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500">결제 시스템은 아직 활성화되지 않았습니다. 실제 충전과 자동 충전은 현재 사용할 수 없습니다.</p>
      </Card>
      <div className="flex flex-wrap gap-2">
        <button disabled className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 text-sm font-bold rounded-full cursor-not-allowed" data-testid="wallet-recharge-disabled">충전하기 (사용 불가)</button>
        <button disabled className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 text-sm font-bold rounded-full cursor-not-allowed" data-testid="wallet-autocharge-disabled">자동충전 설정 (사용 불가)</button>
        <Link to="?section=transactions" className="px-4 py-2 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 text-sm font-bold rounded-full" data-testid="wallet-view-history">충전 내역</Link>
        <Link to="?section=transactions" className="px-4 py-2 bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 text-sm font-bold rounded-full">사용 내역</Link>
        <button disabled className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-500 text-sm font-bold rounded-full cursor-not-allowed" data-testid="wallet-refund-disabled">환불 요청 (사용 불가)</button>
      </div>
      <Card className="p-5">
        <h3 className="font-bold mb-3">카카오 알림 예상 비용</h3>
        <div className="flex flex-wrap items-center gap-2">
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className="px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="notif-channel">
            <option value="kakao_alimtalk">카카오톡 알림</option>
            <option value="sms">문자 알림</option>
            <option value="email">이메일 알림</option>
            <option value="push">앱 푸시</option>
          </select>
          <button onClick={runEstimate} className="px-4 py-2 bg-[#FF3B30] text-white text-sm font-bold rounded-full" data-testid="notif-estimate">예상 비용 계산</button>
        </div>
        {estimate && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="notif-estimate-result">
            <div><p className="text-xs text-gray-500">예상 대상 수</p><p className="text-lg font-black">{estimate.target_count?.toLocaleString()}명</p></div>
            <div><p className="text-xs text-gray-500">건당 단가</p><p className="text-lg font-black">{won(estimate.unit_price)}</p></div>
            <div><p className="text-xs text-gray-500">예상 총비용</p><p className="text-lg font-black">{won(estimate.estimated_cost)}</p></div>
            <div><p className="text-xs text-gray-500">현재 잔액</p><p className="text-lg font-black">{won(estimate.current_balance)}</p></div>
            <div className="col-span-2 md:col-span-4">
              {estimate.sufficient ? (
                <p className="text-xs text-green-600 dark:text-green-400">잔액이 충분합니다.</p>
              ) : (
                <p className="text-xs text-[#FF3B30]" data-testid="notif-insufficient">잔액이 부족하여 예약할 수 없습니다.</p>
              )}
            </div>
          </div>
        )}
      </Card>
      {prices.length > 0 && (
        <Card className="p-5">
          <h3 className="font-bold mb-3">서비스 단가 (관리자 관리)</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            {prices.map((p) => (
              <div key={p.service_type} className="flex justify-between px-3 py-2 bg-black/5 dark:bg-white/5 rounded">
                <span>{p.service_type}</span>
                <span className="font-bold">{won(p.unit_price)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// ============= TRANSACTIONS =============
const TransactionsSection = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get('/seller/wallet/transactions').then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>사용 내역</h2>
      {items.length === 0 ? (
        <EmptyState title="아직 거래 내역이 없습니다." subtitle="충전 후 서비스 이용 시 여기에 표시됩니다." testId="transactions-empty" />
      ) : (
        <Card className="divide-y divide-black/5 dark:divide-white/10" data-testid="transactions-list">
          {items.map((t) => (
            <div key={t.transaction_id} className="p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-sm">{t.transaction_type} {t.service_type && <span className="text-xs text-gray-500 ml-1">· {t.service_type}</span>}</p>
                <p className="text-xs text-gray-500">{format(new Date(t.created_at), 'yyyy-MM-dd HH:mm', { locale: ko })} · {t.status}</p>
              </div>
              <div className="text-right">
                <p className="font-black">{won(t.amount)}</p>
                <p className="text-xs text-gray-500">잔액 {won(t.balance_after)}</p>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
};

// ============= PROFILE =============
// Phase 6.1 HOTFIX · 통합 판매자 프로필 관리 (기본 + 프로필 홈 설정 흡수)
const ProfileSection = ({ seller, refresh }) => {
  const initial = useMemo(() => ({
    // 기본 프로필
    name: seller.name || '', bio: seller.bio || '', profile_photo: seller.profile_photo || '',
    contact_email: seller.contact_email || '', announcements: seller.announcements || '',
    broadcast_schedule_intro: seller.broadcast_schedule_intro || '', is_public: seller.is_public !== false,
    // 프로필 홈 설정 (커버·tagline만 · 상품 노출 옵션은 별도 위치로 이동)
    cover_image_url: seller.cover_image_url || '', tagline: seller.tagline || '',
  }), [seller]);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  useUnsavedGuard(dirty && !saving);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      // 1) 기본 프로필 저장
      await api.put('/seller/profile', {
        name: form.name, bio: form.bio, profile_photo: form.profile_photo,
        contact_email: form.contact_email, announcements: form.announcements,
        broadcast_schedule_intro: form.broadcast_schedule_intro, is_public: form.is_public,
      });
      // 2) 프로필 홈 설정 저장 (커버·tagline만 · 상품 노출은 각 위치에서 저장)
      await api.patch('/seller/profile-home-config', {
        cover_image_url: form.cover_image_url,
        tagline: form.tagline,
      });
      toast.success('프로필이 저장되었습니다.');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-form">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>판매자 프로필</h2>
        {seller.seller_id && (
          <a href={`/seller/${seller.seller_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black" data-testid="profile-view-public">
            내 프로필 보기 ↗
          </a>
        )}
      </div>

      <form onSubmit={save} className="space-y-4">
        {/* ── 기본 프로필 ── */}
        <Card className="p-5 space-y-4">
          <h3 className="text-sm font-black">기본 프로필</h3>
          <div className="max-w-[220px]">
            <ImageUpload value={form.profile_photo} onChange={(url) => setForm({ ...form, profile_photo: url })}
              kind="seller_profile" label="프로필 이미지" aspectClass="aspect-square" testId="prof-profile-img"/>
          </div>
          <input placeholder="셀러명" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="prof-name" />
          <textarea
            placeholder="판매자 상세정보 (프로필 최하단 접기/펼치기 영역에 노출)&#10;예:&#10;상호명: 라이브모아샵&#10;대표자: 홍길동&#10;고객센터: 02-1234-5678&#10;운영시간: 평일 10:00~18:00&#10;이메일: help@example.com&#10;통신판매업 신고번호: 2026-서울○○-0000&#10;반품/교환 안내"
            value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" rows={6} data-testid="prof-bio" />
          <p className="text-[10px] text-gray-500 -mt-3">📌 이 정보는 프로필 최하단 "판매자 상세정보"에 접기/펼치기로 노출됩니다.</p>
          <input placeholder="방송 일정 소개" value={form.broadcast_schedule_intro} onChange={(e) => setForm({ ...form, broadcast_schedule_intro: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
          <input placeholder="연락용 이메일" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" data-testid="prof-email" />
          <textarea placeholder="공지사항" value={form.announcements} onChange={(e) => setForm({ ...form, announcements: e.target.value })} className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" rows={2} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="accent-[#FF3B30]" data-testid="prof-public" /> 프로필 공개
          </label>
        </Card>

        {/* ── 프로필 홈 설정 (Phase 6.1 확장 흡수) ── */}
        <Card className="p-5 space-y-4" data-testid="profile-home-config-card">
          <h3 className="text-sm font-black">🎨 프로필 홈 설정</h3>

          {/* 한 줄 소개 */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">한 줄 소개 (Tagline)</label>
            <input placeholder="예: 여성의류 전문몰 · 매주 화/목 LIVE" maxLength={40}
              value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value.slice(0, 40) })}
              className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
              data-testid="prof-tagline"/>
            <p className="text-[10px] text-gray-500 mt-1">{form.tagline.length}/40자 · 프로필 상단에 이름 아래 1줄로 표시됩니다.</p>
          </div>

          {/* 대표 커버 이미지 · 파일 업로드 */}
          <ImageUpload value={form.cover_image_url} onChange={(url) => setForm({ ...form, cover_image_url: url || '' })}
            kind="seller_cover" label="대표 커버 이미지 (권장 16:6)" aspectClass="aspect-[16/6]" testId="prof-cover-upload"/>

          <p className="text-[11px] text-gray-500 leading-snug pt-1 border-t border-black/5 dark:border-white/10">
            📌 <b>방송 중 판매상품 노출</b> 설정은 <b>LIVE 판매 콘솔</b>에서,{' '}
            <b>전체상품 노출</b> 설정은 <b>상품 관리</b> 메뉴에서 변경할 수 있습니다.
          </p>
        </Card>

        {/* Sticky 저장 */}
        <div className="sticky bottom-3 flex justify-end">
          <button type="submit" disabled={saving || justSaved}
            className="inline-flex items-center gap-1 px-6 py-3 bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-70 text-white font-black rounded-full shadow-lg"
            data-testid="prof-save">
            {justSaved ? (<><Check size={14} /> 저장됨</>) : saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============= SETTINGS =============
const SettingsSectionLegacy = () => (
  <div className="space-y-4">
    <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>설정 (구버전)</h2>
    <p className="text-xs text-gray-500">이 뷰는 곧 제거됩니다. 좌측 <strong>설정</strong> 메뉴를 사용하세요.</p>
  </div>
);

const OrdersSection = () => <SellerOrdersSection />;

const CustomersSection_LEGACY_PLACEHOLDER = () => (
  <div className="space-y-4" data-testid="customers-section-placeholder">
    <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>고객관리</h2>
  </div>
);

// ============= SHELL =============
const SellerDashboardV2 = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [seller, setSeller] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | denied
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openOrderSid, setOpenOrderSid] = useState(null);
  // A4-1G-1B · SimpleProducts 카드/등록 클릭 시 상품 첫 화면 유지한 채 ProductForm modal 만 open.
  //   navigation 을 발생시키지 않으므로 상품관리 화면으로 이동하지 않는다.
  const [homeEditingProduct, setHomeEditingProduct] = useState(null);
  const [homeBroadcasts, setHomeBroadcasts] = useState([]);
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);
  useEffect(() => {
    api.get('/seller/broadcasts')
      .then((r) => setHomeBroadcasts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setHomeBroadcasts([]));
  }, []);

  const params = new URLSearchParams(location.search);
  const active = params.get('section') || 'home';
  const detailFlag = params.get('detail');
  // Determine which top-level tab should be highlighted.
  const activePrimary = SECTIONS.some((s) => s.key === active)
    ? active
    : (LEGACY_TO_PRIMARY[active] || 'home');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/seller/me').then((r) => { setSeller(r.data); setStatus('ok'); }).catch((err) => {
      if (err?.response?.status === 403) setStatus('denied');
      else setStatus('loading');
    });
  }, [user, navigate]);

  const refresh = () => api.get('/seller/me').then((r) => setSeller(r.data));
  // Phase 5.4 — accept an optional extras object of URL params (e.g. { detail: 1 }).
  // The primary `section` key is set first and cannot be overridden by extras.
  const goSection = (key, extras = {}) => {
    const p = new URLSearchParams();
    p.set('section', key);
    Object.entries(extras || {}).forEach(([k, v]) => {
      if (k === 'section') return; // never allow extras to overwrite primary key
      if (v === undefined || v === null || v === '') return;
      // Coerce booleans to '1' (truthy) — some callers pass `detail: true`.
      const val = v === true ? '1' : String(v);
      p.set(k, val);
    });
    navigate(`${location.pathname}?${p.toString()}`);
    setMobileNavOpen(false);
  };

  if (!user) return null;

  if (status === 'loading') {
    return <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF3B30]" /></div>;
  }

  if (status === 'denied') {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center" data-testid="seller-denied">
          <h2 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>판매자 전용 페이지입니다.</h2>
          <p className="text-sm text-gray-500 mb-6">승인된 판매자만 이 페이지에 접근할 수 있습니다.</p>
          <Link to="/" className="text-[#FF3B30] font-bold">홈으로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const renderSection = () => {
    // Phase 5.4 — Simple guided views for the 7 primary tabs.
    // Legacy tabs (analytics, ads, wallet, transactions, profile, settings,
    // followers, broadcasts, oms, fulfillment, rma, inbound, purchase) remain
    // fully functional via URL and are surfaced through the "더보기" hub.
    switch (active) {
      // ── PRIMARY (SIMPLE) VIEWS ──────────────────────────────────────────
      case 'live':
        return detailFlag
          ? <BroadcastsSection />
          : <SimpleLive goTo={goSection} />;
      case 'products':
        return detailFlag
          ? <ProductsSection />
          : (
            <>
              <SimpleProducts
                key={`sp-${homeRefreshKey}`}
                goTo={goSection}
                onOpenDetailedSection={() => goSection('products', { detail: 1 })}
                onOpenProductForm={() => setHomeEditingProduct({ __new: true })}
                onEditProduct={(p) => setHomeEditingProduct(p)}
              />
              {/* A4-1G-1B · 홈 카드/등록 클릭 시 화면 이동 없이 위에 ProductForm modal 만 OPEN.
                  기존 ProductForm 그대로 REUSE (전체 상품관리와 동일 modal). */}
              {homeEditingProduct !== null && (
                <ProductForm
                  initial={homeEditingProduct?.__new ? undefined : homeEditingProduct}
                  broadcasts={homeBroadcasts}
                  onClose={() => setHomeEditingProduct(null)}
                  onSaved={() => { setHomeEditingProduct(null); setHomeRefreshKey((k) => k + 1); }}
                />
              )}
            </>
          );
      case 'orders':
        return <SimpleOrders
                 goTo={goSection}
                 onOpenDetailedSection={(sub) => goSection(typeof sub === 'string' ? sub : 'oms')}
                 onOpenUnifiedDetail={setOpenOrderSid}
               />;
      case 'inventory':
        // A4-1C · UNIFY: 재고 메뉴 진입 시 곧바로 통합 InventorySection 렌더.
        // 이전 SimpleInventory 중간 뷰는 제거 (기능은 모두 InventorySection에
        // 흡수됨: 4-card 필터, 입고/반출 스캔, 대량등록/다운로드/재고조정/이력).
        // 발주 서브뷰 ONLY 는 별도 컴포넌트 유지 (기존 PurchaseOrderSection).
        if (detailFlag === 'inbound')  return <InboundSection />;
        if (detailFlag === 'purchase') return <PurchaseOrderSection />;
        return <InventorySection />;
      case 'customers': {
        // STEP OMS-OUTBOUND-UNIFY-2 · URL `customer_key` 수신 시 CustomersSection 을
        //   detail 뷰로 강제 렌더 + 해당 고객 자동 오픈.  SimpleCustomers 는 목록형.
        const _ck = (params.get('customer_key') || params.get('customer') || '').trim();
        if (_ck) return <CustomersSection initialCustomerKey={_ck} />;
        return detailFlag
          ? <CustomersSection />
          : <SimpleCustomers
              onOpenDetailedSection={() => goSection('customers', { detail: 1 })}
              onOpenUnifiedDetail={setOpenOrderSid}
            />;
      }
      case 'inquiries':
        return <InquiriesCenter />;
      case 'videos':
        return <RecordingLibrary key={params.get('schedule') || 'all'} initialSchedule={params.get('schedule')} />;
      case 'more':
        return <MoreHub onGoRaw={(k) => goSection(k)} />;

      // ── LEGACY / RAW SECTIONS (accessed from 더보기 hub or URL) ─────────
      case 'broadcasts':   return <BroadcastsSection />;
      case 'inbound':      return <InboundSection />;
      case 'purchase':     return <PurchaseOrderSection />;
      case 'orders_legacy':
      case 'oms':          return <OmsWorkflowSection onOpenOrder={setOpenOrderSid} />;
      case 'fulfillment':  return <FulfillmentSection onOpenUnifiedDetail={setOpenOrderSid} />;
      case 'rma':          return <SellerRmaSection />;
      case 'followers':    return <FollowersSection />;
      case 'analytics':    return <AnalyticsSection />;
      case 'ads':          return <AdsSection />;
      case 'wallet':       return <WalletSection />;
      case 'transactions': return <TransactionsSection />;
      case 'profile':      return <ProfileSection seller={seller} refresh={refresh} />;
      case 'settings':     return <SettingsSection />;
      case 'payments':     return <PaymentManagement />;
      default:             return <SimpleHome goTo={goSection} seller={seller} />;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row gap-6">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between gap-2">
          <p className="text-sm font-bold flex-1 truncate">{SECTIONS.find((s) => s.key === activePrimary)?.label}</p>
          {/* STEP 8-LIVE-CONSOLE-UI · Mobile 방송하기 CTA */}
          <Link to="/seller/live-console"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black shadow-sm"
                data-testid="seller-go-live-cta-mobile">
            <Radio size={12} /> 방송
          </Link>
          <button onClick={() => setMobileNavOpen((v) => !v)} className="p-2 rounded border border-black/10 dark:border-white/10" data-testid="seller-mobile-nav-toggle">
            {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Sidebar (desktop) */}
        <aside className="hidden md:block w-56 flex-shrink-0" data-testid="seller-sidebar">
          <Card className="p-3 sticky top-20">
            {/* STEP 8-LIVE-CONSOLE-UI · 눈에 띄는 방송하기 CTA (sidebar 최상단) */}
            <Link to="/seller/live-console"
                  className="w-full mb-3 flex items-center justify-center gap-2 px-3 py-2.5 rounded-full text-sm font-black bg-[#FF3B30] hover:bg-[#D32F2F] text-white transition-colors shadow-sm"
                  data-testid="seller-go-live-cta">
              <Radio size={14} /> 🔴 방송하기
            </Link>
            {process.env.REACT_APP_STUDIO_TEST_ENTRY === '1' && (
              <Link to="/seller/live-console?testmode=1"
                    className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-amber-400 text-amber-700 bg-amber-50 text-[11px] font-black hover:bg-amber-100"
                    aria-label="테스트 모드 진입 (개발용)"
                    data-testid="seller-studio-test-entry">
                🧪 테스트
              </Link>
            )}
            {SECTIONS.map((s) => (
              <button key={s.key} onClick={() => goSection(s.key)} data-testid={`sidebar-${s.key}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors ${
                  activePrimary === s.key ? 'bg-[#FF3B30] text-white' : 'hover:bg-black/5 dark:hover:bg-white/10'
                }`}>
                <s.icon size={16} />
                <span className="flex-1">{s.label}</span>
              </button>
            ))}
            {/* STEP 2-P7-B · 택배/배송 설정 (기존 페이지 REUSE) */}
            <div className="my-2 border-t border-black/10 dark:border-white/10" />
            <Link to="/seller/carrier-accounts"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                  data-testid="sidebar-carrier-accounts">
              <Package size={16} />
              <span className="flex-1">택배 / 배송 설정</span>
            </Link>
            <Link to="/seller/claims"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                  data-testid="sidebar-claims">
              <Package size={16} />
              <span className="flex-1">반품 / 교환</span>
            </Link>
          </Card>
        </aside>

        {/* Mobile nav overlay */}
        {mobileNavOpen && (
          <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setMobileNavOpen(false)}>
            <div className="absolute top-0 right-0 w-64 h-full bg-white dark:bg-[#141414] p-4" onClick={(e) => e.stopPropagation()} data-testid="seller-mobile-nav">
              <button onClick={() => setMobileNavOpen(false)} className="mb-3 p-2"><X size={18} /></button>
              {SECTIONS.map((s) => (
                <button key={s.key} onClick={() => goSection(s.key)} data-testid={`mobile-sidebar-${s.key}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors ${
                    activePrimary === s.key ? 'bg-[#FF3B30] text-white' : 'hover:bg-black/5 dark:hover:bg-white/10'
                  }`}>
                  <s.icon size={16} />
                  <span className="flex-1">{s.label}</span>
                </button>
              ))}
              <div className="my-2 border-t border-black/10 dark:border-white/10" />
              <Link to="/seller/carrier-accounts" onClick={() => setMobileNavOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                    data-testid="mobile-sidebar-carrier-accounts">
                <Package size={16} />
                <span className="flex-1">택배 / 배송 설정</span>
              </Link>
              <Link to="/seller/claims" onClick={() => setMobileNavOpen(false)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-left transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                    data-testid="mobile-sidebar-claims">
                <Package size={16} />
                <span className="flex-1">반품 / 교환</span>
              </Link>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0" data-testid="seller-main">
          {/* Phase 5.1 · Global Search */}
          <div className="mb-4">
            <GlobalSearch
              goSection={goSection}
              onOpenOrder={(sid) => setOpenOrderSid(sid)}
              onOpenCustomer={(ck) => goSection('customers', { customer_key: ck })}
            />
          </div>
          {renderSection()}
        </main>
      </div>

      {/* Phase 5.1 · Unified Order Detail modal (shared across sections) */}
      {openOrderSid && (
        <UnifiedOrderDetail
          sellerOrderId={openOrderSid}
          onClose={() => setOpenOrderSid(null)}
          goSection={goSection}
        />
      )}

      {/* STEP 3-SHIP-D-4/D-5 · Windows Agent → 자동 송장 등록 백그라운드 폴러 */}
      <AgentAutoImport />
    </div>
  );
};

export default SellerDashboardV2;
